import React, {useState, useEffect} from "react";
import DataTable from "examples/Tables/DataTable";
import CrudService from "services/cruds-service";
import {Tooltip, IconButton, TextField, Autocomplete, CircularProgress} from "@mui/material";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import { useNavigate } from "react-router-dom";
import DashboardNavbar from "../../../../../examples/Navbars/DashboardNavbar";
import DashboardLayout from "../../../../../examples/LayoutContainers/DashboardLayout";
import {Chart as ChartJS, CategoryScale, LinearScale, BarElement, Title, Tooltip as ChartTooltip, Legend} from "chart.js";
import Footer from "../../../../../examples/Footer";
import autoTable from "jspdf-autotable";
import {jsPDF} from "jspdf";
import { getTermsText, getTermsTextTR } from "../../../../../constants/quotationTerms";
import { appConfirm } from "components/AppDialog";
import Add from "@mui/icons-material/Add";
import PictureAsPdfOutlined from "@mui/icons-material/PictureAsPdfOutlined";
import {
  CrmPage,
  CrmPageHeader,
  CrmActionButton,
  CrmFilterBar,
  CrmRowActions,
  CrmNotice,
  CRM_COLORS,
} from "../crm/shared/CrmUi";
import { TableCard, SegmentedTabs, TruncatedText } from "../OceanTMS/oceanDashboardUi";

ChartJS.register(CategoryScale, LinearScale, BarElement, Title, ChartTooltip, Legend,);

const MAX_TWO_DECIMAL_FORMATTER = new Intl.NumberFormat("en-US", {
  minimumFractionDigits: 0,
  maximumFractionDigits: 2,
});

const parseAmountValue = (value) => {
  if (typeof value === "number") {
    return Number.isFinite(value) ? value : 0;
  }

  if (value === null || value === undefined || value === "") {
    return 0;
  }

  const rawValue = String(value)
      .trim()
      .replace(/\s/g, "")
      .replace(/[^\d,.-]/g, "");

  if (!rawValue || rawValue === "-" || rawValue === "." || rawValue === ",") {
    return 0;
  }

  const lastCommaIndex = rawValue.lastIndexOf(",");
  const lastDotIndex = rawValue.lastIndexOf(".");
  let normalizedValue = rawValue;

  if (lastCommaIndex !== -1 && lastDotIndex !== -1) {
    if (lastCommaIndex > lastDotIndex) {
      normalizedValue = rawValue.replace(/\./g, "").replace(",", ".");
    } else {
      normalizedValue = rawValue.replace(/,/g, "");
    }
  } else if (lastCommaIndex !== -1) {
    const decimalLength = rawValue.length - lastCommaIndex - 1;
    normalizedValue =
        decimalLength > 0 && decimalLength <= 2
            ? rawValue.replace(/,/g, ".")
            : rawValue.replace(/,/g, "");
  } else if (lastDotIndex !== -1) {
    const dotCount = (rawValue.match(/\./g) || []).length;
    const decimalLength = rawValue.length - lastDotIndex - 1;

    if (dotCount > 1) {
      normalizedValue =
          decimalLength > 0 && decimalLength <= 2
              ? `${rawValue.slice(0, lastDotIndex).replace(/\./g, "")}.${rawValue.slice(lastDotIndex + 1)}`
              : rawValue.replace(/\./g, "");
    }
  }

  const parsedValue = Number(normalizedValue);
  return Number.isFinite(parsedValue) ? parsedValue : 0;
};

const formatAmountMaxTwo = (value) =>
    MAX_TWO_DECIMAL_FORMATTER.format(parseAmountValue(value));

const normalizeCurrencyCode = (value, fallback = "USD") =>
    String(value || fallback || "USD")
        .trim()
        .toUpperCase();

const convertAmountWithRates = (
    amount,
    fromCurrency,
    toCurrency,
    exchangeRates
) => {
  const numericAmount = parseAmountValue(amount);
  const from = normalizeCurrencyCode(fromCurrency, toCurrency || "USD");
  const to = normalizeCurrencyCode(toCurrency, fromCurrency || "USD");

  if (numericAmount === 0 || from === to) {
    return numericAmount;
  }

  if (!exchangeRates) {
    return null;
  }

  const fromRate = from === "USD" ? 1 : Number(exchangeRates[from]);
  const toRate = to === "USD" ? 1 : Number(exchangeRates[to]);

  if (
      !Number.isFinite(fromRate) ||
      fromRate <= 0 ||
      !Number.isFinite(toRate) ||
      toRate <= 0
  ) {
    return null;
  }

  return (numericAmount / fromRate) * toRate;
};

const buildSellingOffers = (quotingTool, exchangeRates) => {
  const transportType = String(quotingTool?.selectedTransportType || "")
      .trim()
      .toUpperCase();

  const pricingMode = String(quotingTool?.oceanPricingMode || "ALL_IN")
      .trim()
      .toUpperCase();

  const fallbackCurrency = normalizeCurrencyCode(
      quotingTool?.currency,
      "USD"
  );

  const fallbackAmount = parseAmountValue(
      quotingTool?.sallingRate ?? quotingTool?.sellingRate ?? 0
  );

  if (transportType !== "OCEAN" || pricingMode !== "BREAKDOWN") {
    return [
      {
        key: `main-${quotingTool?.id || "offer"}`,
        carrierName: "",
        amount: fallbackAmount,
        currency: fallbackCurrency,
        conversionError: false,
        breakdown: [],
      },
    ];
  }

  const carriers = Array.isArray(quotingTool?.carriers)
      ? quotingTool.carriers
      : [];

  const extraCosts = Array.isArray(quotingTool?.extraCosts)
      ? quotingTool.extraCosts
      : [];

  if (!carriers.length) {
    return [
      {
        key: `fallback-${quotingTool?.id || "offer"}`,
        carrierName: "Ocean Offer",
        amount: fallbackAmount,
        currency: fallbackCurrency,
        conversionError: false,
        breakdown: [],
      },
    ];
  }

  return carriers.map((carrierRow, carrierIndex) => {
    const offerCurrency = normalizeCurrencyCode(
        carrierRow?.sellingCarriersRate,
        fallbackCurrency
    );

    const carrierSellingAmount = parseAmountValue(
        carrierRow?.sellingCarriers
    );

    const carrierSellingCurrency = normalizeCurrencyCode(
        carrierRow?.sellingCarriersRate,
        offerCurrency
    );

    const preparedExtraSelling = extraCosts
        .filter(
            (extra) =>
                Boolean(extra?.type) ||
                parseAmountValue(extra?.extraSelling) !== 0
        )
        .map((extra, extraIndex) => {
          const originalAmount = parseAmountValue(extra?.extraSelling);
          const originalCurrency = normalizeCurrencyCode(
              extra?.extraSellingRate,
              offerCurrency
          );

          return {
            key: `extra-${quotingTool?.id || "quote"}-${carrierIndex}-${extraIndex}`,
            label: extra?.type || `Extra Selling #${extraIndex + 1}`,
            originalAmount,
            originalCurrency,
            convertedAmount: convertAmountWithRates(
                originalAmount,
                originalCurrency,
                offerCurrency,
                exchangeRates
            ),
          };
        });

    const conversionError = preparedExtraSelling.some(
        (item) => item.convertedAmount === null
    );

    const convertedExtraTotal = conversionError
        ? null
        : preparedExtraSelling.reduce(
            (total, item) => total + parseAmountValue(item.convertedAmount),
            0
        );

    const offerTotal = conversionError
        ? null
        : carrierSellingAmount + convertedExtraTotal;

    return {
      key:
          carrierRow?.id ||
          `carrier-${quotingTool?.id || "quote"}-${carrierIndex}`,
      carrierName:
          carrierRow?.name ||
          `Carrier #${carrierIndex + 1}`,
      amount: offerTotal,
      currency: offerCurrency,
      conversionError,
      breakdown: [
        {
          key: `carrier-selling-${quotingTool?.id || "quote"}-${carrierIndex}`,
          label: carrierRow?.chargeType || "Ocean Freight Charge",
          originalAmount: carrierSellingAmount,
          originalCurrency: carrierSellingCurrency,
        },
        ...preparedExtraSelling,
      ],
    };
  });
};
function QuotingToolTable(quotingTool) {
  const [data, setData] = useState([]);
  const [notification, setNotification] = useState({ value: false, text: "" });
  const navigate = useNavigate();
  const [customerEmails, setCustomerEmails] = useState({});
  const [customerPaymentMap, setCustomerPaymentMap] = useState({});
  const [agentPaymentMap, setAgentPaymentMap] = useState({});
  const [tableExchangeRates, setTableExchangeRates] = useState(null);
  const [tableRatesLoading, setTableRatesLoading] = useState(true);

  useEffect(() => {
    let isMounted = true;

    const loadTableExchangeRates = async () => {
      setTableRatesLoading(true);

      try {
        const response = await fetch(
            "https://api.exchangerate-api.com/v4/latest/USD"
        );

        if (!response.ok) {
          throw new Error(
              `Exchange rate request failed: ${response.status}`
          );
        }

        const result = await response.json();
        const rates =
            result?.rates && typeof result.rates === "object"
                ? result.rates
                : null;

        if (isMounted) {
          setTableExchangeRates(rates);
        }
      } catch (error) {
        console.error(
            "Quoting Tool table exchange rates could not be loaded:",
            error
        );

        if (isMounted) {
          setTableExchangeRates(null);
        }
      } finally {
        if (isMounted) {
          setTableRatesLoading(false);
        }
      }
    };

    loadTableExchangeRates();

    return () => {
      isMounted = false;
    };
  }, []);

  // ✅ ARRAY olmalı (Autocomplete için)
  const statusOptions = [
    { value: "Pending",      label: "Pending",      color: "#FFB300" }, // amber
    { value: "Sent",         label: "Sent",         color: "#1E88E5" }, // blue
    { value: "Secured",      label: "Secured",      color: "#43A047" }, // green
    { value: "Lost",         label: "Lost",         color: "#E53935" }, // red
    { value: "Closed",       label: "Closed",       color: "#546E7A" }, // grey-blue
  ];

  function StatusCell({ row, onLocalUpdate }) {
    const [saving, setSaving] = React.useState(false);

    const currentOption =
        statusOptions.find((o) => o.value === row.status || o.label === row.status) ?? statusOptions[0];

    const handleChange = async (_, newValue) => {
      const newLabel = newValue?.value ?? newValue?.label ?? "";
      if (!newLabel || newLabel === row.status) return;

      onLocalUpdate(row.id, newLabel);
      setSaving(true);
      try {
        await CrudService.updateQuotingTool({ status: newLabel }, row.id);
      } catch (e) {
        onLocalUpdate(row.id, row.status);
        console.error(e);
      } finally {
        setSaving(false);
      }
    };

    const Dot = ({ color }) => (
        <span
            style={{
              display: "inline-block",
              width: 10,
              height: 10,
              borderRadius: "50%",
              backgroundColor: color || "#999",
              marginRight: 8,
              flexShrink: 0,
            }}
        />
    );

    return (
        <Autocomplete
            options={statusOptions}
            value={currentOption}
            onChange={handleChange}
            disableClearable
            size="small"
            getOptionLabel={(o) => o.label}
            isOptionEqualToValue={(opt, val) => opt?.value === val?.value}
            renderOption={({ key: optionKey, ...props }, option) => (
                <li key={optionKey} {...props} style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  <Dot color={option.color} />
                  <span>{option.label}</span>
                </li>
            )}
            renderInput={(params) => (
                <TextField
                    {...params}
                    variant="outlined"
                    placeholder="Status"
                    InputProps={{
                      ...params.InputProps,
                      startAdornment: (
                          <>
                            <Dot color={currentOption?.color} />
                            {params.InputProps.startAdornment}
                          </>
                      ),
                      endAdornment: (
                          <>
                            {saving ? <CircularProgress size={16} /> : null}
                            {params.InputProps.endAdornment}
                          </>
                      ),
                    }}
                />
            )}
            sx={{ minWidth: 170 }}
        />
    );
  }
  const handleLocalStatusUpdate = (id, newLabel) => {
    setData(prev =>
        prev.map(item => (item.id === id ? { ...item, status: newLabel } : item))
    );
  };
  useEffect(() => {
    const fetchCustomers = async () => {
      try {
        const response = await CrudService.getCustomers();
        const emails = {};
        const paymentMap = {};

        response.data.forEach((customer) => {
          const attrs = customer.attributes || {};
          const customerName = attrs.name || "Unknown Customer";

          if (attrs.email) emails[customerName.trim().toLowerCase()] = attrs.email;

          const key = normalizeKey(customerName);
          const financialBlock = attrs.financialInfoByProduct?.[0];

          paymentMap[key] = {
            road:
                financialBlock?.financialTerms?.road?.paymentTerms ??
                financialBlock?.paymentTerms ??
                0,
            ocean:
                financialBlock?.financialTerms?.ocean?.paymentTerms ??
                0,
            air:
                financialBlock?.financialTerms?.air?.paymentTerms ??
                0,
          };
        });

        setCustomerEmails(emails);
        setCustomerPaymentMap(paymentMap);
      } catch (error) {
        console.error("Müşteri verileri alınırken hata oluştu:", error);
      }
    };

    fetchCustomers();
  }, []);

  useEffect(() => {
    const fetchGlobalAgents = async () => {
      try {
        const response = await CrudService.getGlobalAgents();

        const rawData = response.data?.data || response.data || [];
        const paymentMap = {};

        rawData.forEach((agent) => {
          const attrs = agent.attributes || agent;
          const agentName = attrs.name || "";

          if (!agentName) return;

          const key = normalizeKey(agentName);
          const financialBlock = attrs.financialInfoByProduct?.[0];

          paymentMap[key] = {
            road:
                financialBlock?.financialTerms?.road?.paymentTerms ??
                financialBlock?.paymentTerms ??
                0,
            ocean:
                financialBlock?.financialTerms?.ocean?.paymentTerms ??
                0,
            air:
                financialBlock?.financialTerms?.air?.paymentTerms ??
                0,
          };
        });

        setAgentPaymentMap(paymentMap);
      } catch (error) {
        console.error("Global agent verileri alınırken hata oluştu:", error);
      }
    };

    fetchGlobalAgents();
  }, []);


  useEffect(() => {
    const fetchQuotingTools = async () => {
      try {
        const response = await CrudService.getQuotingTools();
        const formattedData = response.data.map((item) => ({
          id: item.id,
          ...item.attributes,
          customerEmail: item.attributes.customerEmail || "",
        }));

        setData(formattedData);
      } catch (error) {
        console.error("Error fetching quoting tools:", error);
        setNotification({
          value: true,
          text: "Failed to load quote tools. Please try again later.",
        });
      }
    };

    fetchQuotingTools();
  }, []);
  const [salesRepOptions, setSalesRepOptions] = useState([]);
  useEffect(() => {
    const fetchSalesReps = async () => {
      try {
        const response = await CrudService.getUsers();
        const users = response.data.map((u) => ({
          id: u.id,
          name: u.attributes?.name || "Unknown",
        }));
        setSalesRepOptions(users);
      } catch (error) {
        console.error("Error fetching sales reps:", error);
      }
    };
    fetchSalesReps();
  }, []);

  function loadImageAsBase64(url) {
    return new Promise((resolve, reject) => {
      fetch(url)
          .then((res) => res.blob())
          .then((blob) => {
            const reader = new FileReader();
            reader.onloadend = () => resolve(reader.result);
            reader.onerror = reject;
            reader.readAsDataURL(blob);
          })
          .catch(reject);
    });
  }


  const normalizeKey = (value) =>
      String(value || "")
          .trim()
          .toUpperCase();

  const getTransportModeKey = (transportType) => {
    const mode = String(transportType || "").toLowerCase();

    if (mode.includes("road")) return "road";
    if (mode.includes("ocean") || mode.includes("sea")) return "ocean";
    if (mode.includes("air")) return "air";

    return "road";
  };

  const getPaymentDaysFromEntity = (entity, modeKey) => {
    const financialBlock = entity?.financialInfoByProduct?.[0];

    return (
        financialBlock?.financialTerms?.[modeKey]?.paymentTerms ??
        financialBlock?.paymentTerms ??
        0
    );
  };

  const getQuotePaymentDays = (quotingToolData) => {
    const modeKey = getTransportModeKey(quotingToolData.selectedTransportType);

    const customerName = normalizeKey(quotingToolData.customer);

    const agentName = normalizeKey(
        quotingToolData.agent ||
        quotingToolData.globalAgent ||
        quotingToolData.globalAgentName ||
        quotingToolData.agentName ||
        quotingToolData.selectedAgent
    );

    const customerDays = Number(customerPaymentMap[customerName]?.[modeKey] ?? 0);
    const agentDays = Number(agentPaymentMap[agentName]?.[modeKey] ?? 0);

    if (customerDays > 0 && agentDays > 0) {
      return `Customer: ${customerDays} days / Agent: ${agentDays} days`;
    }

    if (customerDays > 0) {
      return `${customerDays} days`;
    }

    if (agentDays > 0) {
      return `${agentDays} days`;
    }

    return "As Below";
  };
  const safeText = (v) => {
    const s = String(v ?? "N/A");

    // NFKD sonrası bile "tek karakter" kalanlar (decompose olmayanlar) + özel durumlar
    const specialMap = {
      // TR (normalize sonrası i problemi)
      "İ": "I",
      "ı": "i",

      // EU common (bazıları decompose olmaz)
      "Ł": "L",
      "ł": "l",
      "ß": "ss",
      "Æ": "AE",
      "æ": "ae",
      "Ø": "O",
      "ø": "o",
      "Œ": "OE",
      "œ": "oe",

      // Ek sık görülenler
      "Đ": "D",
      "đ": "d",
      "Þ": "TH",
      "þ": "th",
      "Ð": "D",
      "ð": "d",
      "Ĳ": "IJ",
      "ĳ": "ij",
    };

    return s
        .normalize("NFKD")                       // harfi + diacritics (ayırabildiklerini ayırır)
        .replace(/[\u0300-\u036f]/g, "")         // tüm diacritics/aksanları kaldır (ń -> n)
        .replace(/[^\0]/gu, (ch) => specialMap[ch] ?? ch) // kalan özel harfleri düzelt
        .replace(/\u0000/g, "")
        .replace(/[\u200B-\u200D\uFEFF]/g, "")
        .replace(/\s+/g, " ")
        .trim();
  };

  const generatePDF = async (quotingToolData) => {
    const quoteType = quotingToolData.quoteType;
    const selectedTransportType = quotingToolData.selectedTransportType;
    const termsAndConditionsText = getTermsText(quotingToolData) || "Terms & Conditions not available.";
    const termsAndConditionsTextTR = getTermsTextTR(quotingToolData) || "Şartlar & Koşullar bulunamadı.";
    const transportTitleMap = {
      Road: "Road Transport",
      Ocean: "Ocean Freight",
      Air: "Air Freight",
      Multimodal: "Multimodal Transport",
      ProjectCargo: "Project Cargo",
    };

    const transportTitle =
        transportTitleMap[selectedTransportType] || "Transport";
    const toNumber = (v) => {
      if (v === null || v === undefined) return 0;
      const s = String(v).trim();
      if (!s) return 0;
      const m = s.match(/-?[\d.,]+/);
      if (!m) return 0;
      let n = m[0];
      if (n.includes(".") && n.includes(",")) {
        n = n.replace(/\./g, "").replace(",", ".");
      } else if (n.includes(",") && !n.includes(".")) {
        n = n.replace(",", ".");
      } else {
        n = n.replace(/,/g, "");
      }
      const out = Number(n);
      return Number.isFinite(out) ? out : 0;
    };
    const fmtMoney = (amount, currency) => {
      const n = Number(amount || 0);
      return `${formatAmountMaxTwo(n)} ${currency || ""}`.trim();
    };
    const roadFee = toNumber(quotingToolData.sallingRate);
    const originFee = toNumber(quotingToolData.originSallingRate);
    const destFee = toNumber(quotingToolData.destinationSallingRate);
    const cur1 = quotingToolData.currency || "";
    const cur2 = quotingToolData.originCurrency || "";
    const cur3 = quotingToolData.destinationCurrency || "";
    const sameCurrency = cur1 && cur1 === cur2 && cur1 === cur3;
    const totalCurrency = sameCurrency ? cur1 : "";
    const totalFee = roadFee + originFee + destFee;
    const doc = new jsPDF();
    const loadFontAsBase64 = async (url) => {
      const res = await fetch(url);
      const buf = await res.arrayBuffer();
      let binary = "";
      const bytes = new Uint8Array(buf);
      const chunk = 0x8000;
      for (let i = 0; i < bytes.length; i += chunk) {
        binary += String.fromCharCode(...bytes.subarray(i, i + chunk));
      }
      return btoa(binary);
    };

    const registerTurkishFont = async () => {
      const regular = await loadFontAsBase64("/fonts/Roboto-Regular.ttf");
      const bold = await loadFontAsBase64("/fonts/Roboto-Bold.ttf");

      doc.addFileToVFS("Roboto-Regular.ttf", regular);
      doc.addFont("Roboto-Regular.ttf", "Roboto", "normal", "Identity-H"); // ✅

      doc.addFileToVFS("Roboto-Bold.ttf", bold);
      doc.addFont("Roboto-Bold.ttf", "Roboto", "bold", "Identity-H"); // ✅

      doc.setFont("Roboto", "normal"); // ✅ default
    };


    await registerTurkishFont();

    const base64BackgroundImage = await loadImageAsBase64("/images/letterhead.png");
    const backgroundImage = new Image();
    backgroundImage.src = `data:image/png;base64,${base64BackgroundImage}`;
    const formatDate = (isoString) => {
      if (!isoString) return "N/A";

      const quoteDate = new Date(isoString);
      const now = new Date();

      return quoteDate.toLocaleDateString("tr-TR", {
            year: "numeric",
            month: "2-digit",
            day: "2-digit",
          }) + " " +
          now.toLocaleTimeString("tr-TR", {
            hour: "2-digit",
            minute: "2-digit",
            second: "2-digit",
            hour12: false,
          });
    };
    const bgDrawnPages = new Set();
    const PAGE_TOP_MARGIN = 30;
    const PAGE_BOTTOM_MARGIN = 30;
    const DEFAULT_TABLE_GAP = 5;

    const addBackgroundToPageOnce = () => {
      const page = doc.getCurrentPageInfo().pageNumber;
      if (bgDrawnPages.has(page)) return;
      bgDrawnPages.add(page);
      doc.addImage(base64BackgroundImage, "PNG", 0, 0, 210, 297);
    };

    const getPageHeight = () => doc.internal.pageSize.getHeight();
    const getUsablePageBottom = () => getPageHeight() - PAGE_BOTTOM_MARGIN;

    const ensurePdfSpace = (
        requestedStartY,
        minimumRequiredHeight = 35
    ) => {
      const safeStartY = Number(
          requestedStartY || PAGE_TOP_MARGIN
      );

      if (
          safeStartY + minimumRequiredHeight >
          getUsablePageBottom()
      ) {
        doc.addPage();
        addBackgroundToPageOnce();

        return PAGE_TOP_MARGIN;
      }

      return safeStartY;
    };

    const getNextTableStartY = (
        gap = DEFAULT_TABLE_GAP,
        minimumRequiredHeight = 35
    ) => {
      const previousFinalY = Number(doc.lastAutoTable?.finalY);
      const desiredStartY = Number.isFinite(previousFinalY)
          ? previousFinalY + gap
          : PAGE_TOP_MARGIN;

      return ensurePdfSpace(desiredStartY, minimumRequiredHeight);
    };

    addBackgroundToPageOnce();

    const autoTableWithBg = (options = {}) => {
      const {
        startY,
        minimumRequiredHeight = 35,
        margin,
        styles,
        rowPageBreak = "avoid",
        showHead = "everyPage",
        willDrawPage,
        didDrawPage,
        ...restOptions
      } = options;

      const safeStartY = ensurePdfSpace(startY, minimumRequiredHeight);

      autoTable(doc, {
        ...restOptions,
        startY: safeStartY,
        margin: {
          top: PAGE_TOP_MARGIN,
          bottom: PAGE_BOTTOM_MARGIN,
          left: 14,
          right: 14,
          ...(margin || {}),
        },
        pageBreak: "auto",
        rowPageBreak,
        showHead,
        styles: {
          overflow: "linebreak",
          valign: "middle",
          ...(styles || {}),
        },
        willDrawPage: (data) => {
          addBackgroundToPageOnce();
          if (typeof willDrawPage === "function") willDrawPage(data);
        },
        didDrawPage: (data) => {
          if (typeof didDrawPage === "function") didDrawPage(data);
        },
      });

      return doc.lastAutoTable?.finalY || safeStartY;
    };
    const customerText = safeText(quotingToolData.customer || "Unknown Customer");
    doc.setFont("helvetica", "normal");
    doc.setFontSize(9);
    doc.text(customerText, 14, 45);
    doc.setFont("helvetica", "bold");
    doc.setFontSize(7);
    doc.text("FiLENDERS LOJiSTiK VE TEKNOLOJi A.S.", 138, 15);
    doc.setFont("helvetica", "normal");
    doc.setFontSize(7);
    doc.text(
        "CIFTLIKKOY MAH. MERSIN UNIVERSITESI KAMPUS \n" +
        "ALANI KUME EVLER TEKNOPARK TEKNOPARK NO:\n" +
        "35B IC KAPI NO: B02 YENISEHIR MERSIN / TURKIYE\n" +
        "pricing@filenders.com   +90 324 503 8433",
        138,
        20);
    doc.setFont("helvetica", "bold");
    doc.setFontSize(9);
    doc.setTextColor(255, 140, 0);
    doc.textWithLink("www.filenders.com", 138, 33, {url: "https://www.filenders.com"});
    doc.setFont("helvetica", "normal");
    doc.setTextColor(0, 0, 0);
    doc.setDrawColor(0);
    doc.line(10, 35, 200, 35);
    doc.setFont("helvetica", "normal");
    doc.setFontSize(8);
    const customerY = 45;
    const messageText = `
Hello,

Our prices for ${transportTitle} offer are presented below for your information.
We wish you a positive cooperation and hope to work together.

Have a nice day.

Description: ${quotingToolData.description || ""}
`;
    const messageY = customerY + 10;
    const splitMessageText = doc.splitTextToSize(messageText, 180);
    doc.text(splitMessageText, 15, messageY);
    const paymentDaysText = getQuotePaymentDays(quotingToolData);

    const tableData = [
      ["Quote No", quotingToolData.quoteNo || "N/A"],
      ["Quote Date", formatDate(quotingToolData.quoteDate)],
      ["Valid Until", formatDate(quotingToolData.estimatedCloseDate)],
      ["Incoterm", quotingToolData.incoterm || ""],
      ["Service", quotingToolData.service || "N/A"],
      ["Payment Days", paymentDaysText],
    ];

    autoTableWithBg( {
      startY: 37,
      margin: {left: 140},
      head: [["Quote Details", ""]],
      body: tableData,
      theme: "grid",
      styles: {
        fontSize: 8,
        cellPadding: 5, // Hücre içi boşluk
        lineWidth: 0.7
      },
      headStyles: {
        fillColor: [16, 28, 54],
        textColor: [255, 255, 255],
        halign: "right",
        lineWidth: 0.7, // Çizgi kalınlığı
        lineColor: [192, 192, 192], // Çizgi rengi siyah
      },
      bodyStyles: {
        fontSize: 8,
        lineWidth: 0.7,
        halign: "left",
        cellPadding: 3,
      },
      drawHeaderRow: (row, data) => {
        data.table.lineWidth = 0;
      },
    });
    let offerDetails = [];
    if (selectedTransportType === "Road") {
      const TR = (s) => (s ?? "").toString();




      const decodeHtmlEntities = (str) => {
        // Browser ortamında en güvenlisi
        const txt = document.createElement("textarea");
        txt.innerHTML = String(str ?? "");
        return txt.value;
      };

// "GdaÅ„sk" gibi UTF-8 → latin1 mojibake durumlarını toparlamak için
      const fixMojibake = (str) => {
        const s = String(str ?? "");
        // bu karakterler sık görünürse dene
        if (!/[ÃÅÂ]/.test(s)) return s;

        try {
          const bytes = Uint8Array.from(s, (c) => c.charCodeAt(0));
          const decoded = new TextDecoder("utf-8", { fatal: false }).decode(bytes);

          // Heuristik: decode sonrası "Ã/Å" azalıyorsa bunu kullan
          const score = (x) => (x.match(/[ÃÅÂ]/g) || []).length;
          return score(decoded) < score(s) ? decoded : s;
        } catch {
          return s;
        }
      };

      const safeText = (v) => {
        let s = String(v ?? "N/A");

        // 1) entity decode (bazı datalar 2 kere encode olabiliyor)
        s = decodeHtmlEntities(s);
        s = decodeHtmlEntities(s);

        // 2) mojibake düzeltme
        s = fixMojibake(s);

        // 3) özel karakter map (decompose olmayanlar + TR özel durumlar)
        const specialMap = {
          "İ": "I",
          "ı": "i",
          "Ł": "L",
          "ł": "l",
          "ß": "ss",
          "Æ": "AE",
          "æ": "ae",
          "Ø": "O",
          "ø": "o",
          "Œ": "OE",
          "œ": "oe",
          "Đ": "D",
          "đ": "d",
          "Þ": "TH",
          "þ": "th",
          "Ð": "D",
          "ð": "d",
          "Ĳ": "IJ",
          "ĳ": "ij",
        };

        // 4) normalize + diacritics temizle
        s = s
            .normalize("NFKD")
            .replace(/[\u0300-\u036f]/g, "");

        // 5) kalan özel harfleri geçir
        s = s.replace(/[\s\S]/g, (ch) => specialMap[ch] ?? ch);

        return s
            .replace(/\u0000/g, "")
            .replace(/[\u200B-\u200D\uFEFF]/g, "")
            .replace(/\s+/g, " ")
            .trim();
      };

      const pdfText = (v) =>
          decodeHtmlEntities(String(v ?? ""))
              .replace(/\u0000/g, "")
              .replace(/[\u200B-\u200D\uFEFF]/g, "")
              .replace(/\s+/g, " ")
              .trim();

      let currentY = (doc.lastAutoTable?.finalY || 55) + 5;


      const joinParts = (...parts) =>
          parts
              .map((x) => safeText(x))
              .filter((x) => x && x !== "N/A")
              .join(", ");

      const formatLoc = (loc = {}) => {
        const { additionalInfo, city, district, zipcode, country } = loc || {};
        return joinParts(additionalInfo, city, district, zipcode, country) || "N/A";
      };

      const formatCustoms = (loc = {}) => {
        const { additionalInfo, country } = loc || {};
        return joinParts(additionalInfo, country) || "N/A";
      };

      const pairs = [
        ["Pickup Address", formatLoc(quotingToolData.pickupLocation)],
        ["Exit Customs", formatCustoms(quotingToolData.exportLocation)],
        ["Arrival Customs", formatCustoms(quotingToolData.importLocation)],
        ["Delivery Address", formatLoc(quotingToolData.deliveryLocation)],
        ["Commodity", pdfText(quotingToolData.commodity || "N/A")],
        ["Gross Weight (kg)", pdfText(quotingToolData.grossWeight ?? "N/A")],
        ["Chargeable Weight (kg)", pdfText(quotingToolData.chargeableWeight ?? "N/A")],
        ["Estimated Transit Time (day)", pdfText(quotingToolData.transitTime ?? "N/A")],
      ];

      const left = pairs.slice(0, 4);
      const right = pairs.slice(4, 8);
      const leftover = pairs.slice(8);

      const rows = [];
      const rowCount = Math.max(left.length, right.length);

      for (let i = 0; i < rowCount; i++) {
        const L = left[i] || ["", ""];
        const R = right[i] || ["", ""];
        rows.push([TR(L[0]), TR(L[1] || "N/A"), TR(R[0]), TR(R[1] || "N/A")]);
      }

      leftover.forEach(([k, v]) => {
        rows.push([
          { content: TR(k), styles: { fontStyle: "bold" } },
          { content: TR(v || "N/A"), colSpan: 3 },
        ]);
      });

      autoTableWithBg({
        startY: currentY,
        head: [[{ content: "Cargo Details", colSpan: 4 }]],
        body: rows,
        theme: "grid",
        styles: { fontSize: 8, cellPadding: 4, lineWidth: 0.7 },
        headStyles: {
          fillColor: [16, 28, 54],
          textColor: [255, 255, 255],
          halign: "center",
        },
        columnStyles: {
          0: { fontStyle: "bold", cellWidth: 45 },
          1: { cellWidth: 75 },
          2: { fontStyle: "bold", cellWidth: 45 },
          3: { cellWidth: "auto" },
        },
      });

      currentY = getNextTableStartY(5, 40);

      /* ---------------- FEES (ALL-IN / BREAKDOWN) ---------------- */

      const sellingCurrency = quotingToolData.currency;
      const totalSelling = Number(quotingToolData.sallingRate || 0);

      let feeRows = [];

      const pricingMode = quotingToolData.roadPricingMode || "ALL_IN";

      if (pricingMode === "ALL_IN") {
        // ✅ ALL-IN
        feeRows = [
          [
            { content: "Estimated All-in Rate", styles: { fontStyle: "bold" } },
            {
              content: `${formatAmountMaxTwo(totalSelling)} ${sellingCurrency}`,
              styles: { fontStyle: "bold", halign: "right" }
            }
          ]
        ];
      } else {
        const carrierRows = (quotingToolData.carriers || []).map(c => [
          `${c.chargeType ? ` ${c.chargeType}` : ""}`,
          `${formatAmountMaxTwo(c.sellingCarriers || 0)} ${sellingCurrency}`
        ]);

        const extraRows = (quotingToolData.extraCosts || []).map(e => [
          `Extra Charge (${safeText(e.type || "Extra")})`,
          `${formatAmountMaxTwo(e.extraSelling || 0)} ${sellingCurrency}`
        ]);

        feeRows = [
          ...carrierRows,
          ...extraRows,
          [
            { content: "Estimated Rates", styles: { fontStyle: "bold" } },
            {
              content: `${formatAmountMaxTwo(totalSelling)} ${sellingCurrency}`,
              styles: { fontStyle: "bold", halign: "right" }
            }
          ]
        ];
      }


      autoTableWithBg({
        startY: currentY,
        head: [[{ content: "Charges Details", colSpan: 2 }]],
        body: feeRows,
        theme: "grid",
        styles: {
          fontSize: 8,
          cellPadding: 4,
          lineWidth: 0.7
        },
        headStyles: {
          fillColor: [16, 28, 54],
          textColor: [255, 255, 255],
          halign: "center"
        },
        bodyStyles: {
          halign: "left",
          valign: "middle"
        },
        columnStyles: {
          0: { cellWidth: 110 },
          1: { cellWidth: "auto", halign: "right" }
        },
      });
    }

    else if (selectedTransportType === "Ocean") {
      let currentY = getNextTableStartY(5, 55);

      const carriers = Array.isArray(quotingToolData.carriers)
          ? quotingToolData.carriers
          : [];

      const extraCosts = Array.isArray(quotingToolData.extraCosts)
          ? quotingToolData.extraCosts
          : [];

      const firstCarrier = carriers[0] || {};

      const normalizeCurrency = (
          value,
          fallback = "USD"
      ) =>
          String(value || fallback || "USD")
              .trim()
              .toUpperCase();

      const pickFirstValue = (...values) => {
        const selectedValue = values.find(
            (value) =>
                value !== null &&
                value !== undefined &&
                String(value).trim() !== ""
        );

        return selectedValue ?? "N/A";
      };

      const joinOceanParts = (...parts) => {
        const result = parts
            .map((part) =>
                part === null || part === undefined
                    ? ""
                    : safeText(part)
            )
            .filter(
                (part) =>
                    part &&
                    part !== "N/A"
            )
            .join(" / ");

        return result || "N/A";
      };

      const normalizeServiceName = (service) =>
          String(service || "Port to Port")
              .trim()
              .replace(/\s+/g, " ");

      const normalizedService =
          normalizeServiceName(
              quotingToolData.service
          );

      const showPickupAddress = [
        "Door to Port",
        "Door to Door",
      ].includes(normalizedService);

      const showDeliveryAddress = [
        "Port to Door",
        "Door to Door",
      ].includes(normalizedService);

      const formatOceanDoorAddress = (
          structuredAddress,
          stringAddress = ""
      ) => {
        if (
            structuredAddress &&
            typeof structuredAddress === "object" &&
            !Array.isArray(structuredAddress)
        ) {
          const addressText = joinOceanParts(
              structuredAddress.country,
              structuredAddress.city,
              structuredAddress.additionalInfo
          );

          if (addressText !== "N/A") {
            return addressText;
          }
        }

        const fallbackText = String(
            stringAddress || ""
        ).trim();

        return fallbackText
            ? safeText(fallbackText)
            : "N/A";
      };

      const pickupAddressText =
          formatOceanDoorAddress(
              quotingToolData.pickupAddress1 ||
              quotingToolData.pickupAddressDetail,
              quotingToolData.pickupAddress
          );

      const deliveryAddressText =
          formatOceanDoorAddress(
              quotingToolData.deliveryAddress2 ||
              quotingToolData.deliveryAddressDetail,
              quotingToolData.deliveryAddress
          );

      /*
       * Kur bilgileri henüz yüklenmemişse
       * PDF oluşturulurken bir kez alınır.
       */
      let oceanExchangeRates =
          tableExchangeRates;

      if (!oceanExchangeRates) {
        try {
          const exchangeResponse = await fetch(
              "https://api.exchangerate-api.com/v4/latest/USD"
          );

          if (!exchangeResponse.ok) {
            throw new Error(
                `Exchange rate request failed: ${exchangeResponse.status}`
            );
          }

          const exchangeData =
              await exchangeResponse.json();

          oceanExchangeRates =
              exchangeData?.rates &&
              typeof exchangeData.rates === "object"
                  ? exchangeData.rates
                  : null;
        } catch (error) {
          console.error(
              "Ocean PDF exchange rates could not be loaded:",
              error
          );
        }
      }

      /*
       * Source Currency -> USD -> Target Currency
       */
      const convertOceanAmount = (
          amount,
          fromCurrency,
          toCurrency
      ) => {
        const numericAmount =
            toNumber(amount);

        const from = normalizeCurrency(
            fromCurrency,
            toCurrency || "USD"
        );

        const to = normalizeCurrency(
            toCurrency,
            fromCurrency || "USD"
        );

        if (numericAmount === 0) {
          return 0;
        }

        if (from === to) {
          return numericAmount;
        }

        const fromRate =
            from === "USD"
                ? 1
                : Number(
                    oceanExchangeRates?.[from]
                );

        const toRate =
            to === "USD"
                ? 1
                : Number(
                    oceanExchangeRates?.[to]
                );

        if (
            !Number.isFinite(fromRate) ||
            fromRate <= 0 ||
            !Number.isFinite(toRate) ||
            toRate <= 0
        ) {
          console.error(
              `Currency conversion failed: ${from} -> ${to}`
          );

          return null;
        }

        const amountInUSD =
            numericAmount / fromRate;

        return amountInUSD * toRate;
      };

      const oceanContainerType =
          pickFirstValue(
              firstCarrier.containerTypes,
              firstCarrier.containerType,
              quotingToolData.container
          );

      const oceanTransitTime =
          pickFirstValue(
              firstCarrier.transitTime,
              quotingToolData.transitTime
          );

      const polText = joinOceanParts(
          firstCarrier.polCountry,
          firstCarrier.pol
      );

      const podText = joinOceanParts(
          firstCarrier.podCountry,
          firstCarrier.pod
      );

      const quoteTypeText =
          [
            quotingToolData.quoteType,
            quotingToolData.oceanTransportType,
          ]
              .map((value) =>
                  String(value || "").trim()
              )
              .filter(Boolean)
              .join(", ") || "N/A";

      const grossWeightText = safeText(
          pickFirstValue(
              quotingToolData.grossWeight
          )
      );

      /* ---------------- OCEAN DETAILS ---------------- */

      let oceanDetailRows = [];

      if (
          normalizedService === "Port to Port"
      ) {
        oceanDetailRows = [
          [
            "POL",
            safeText(polText),
            "Container Type",
            safeText(oceanContainerType),
          ],
          [
            "POD",
            safeText(podText),
            "Quote Type",
            safeText(quoteTypeText),
          ],
          [
            "Gross Weight (kg)",
            grossWeightText,
            "Transit Time (day)",
            safeText(oceanTransitTime),
          ],
        ];
      } else if (
          normalizedService === "Door to Port"
      ) {
        oceanDetailRows = [
          [
            "Pickup Address",
            safeText(pickupAddressText),
            "Container Type",
            safeText(oceanContainerType),
          ],
          [
            "POL",
            safeText(polText),
            "Quote Type",
            safeText(quoteTypeText),
          ],
          [
            "POD",
            safeText(podText),
            "Gross Weight (kg)",
            grossWeightText,
          ],
          [
            "Transit Time (day)",
            safeText(oceanTransitTime),
            "",
            "",
          ],
        ];
      } else if (
          normalizedService === "Port to Door"
      ) {
        oceanDetailRows = [
          [
            "POL",
            safeText(polText),
            "Container Type",
            safeText(oceanContainerType),
          ],
          [
            "POD",
            safeText(podText),
            "Quote Type",
            safeText(quoteTypeText),
          ],
          [
            "Delivery Address",
            safeText(deliveryAddressText),
            "Gross Weight (kg)",
            grossWeightText,
          ],
          [
            "Transit Time (day)",
            safeText(oceanTransitTime),
            "",
            "",
          ],
        ];
      } else if (
          normalizedService === "Door to Door"
      ) {
        oceanDetailRows = [
          [
            "Pickup Address",
            safeText(pickupAddressText),
            "Container Type",
            safeText(oceanContainerType),
          ],
          [
            "POL",
            safeText(polText),
            "Quote Type",
            safeText(quoteTypeText),
          ],
          [
            "POD",
            safeText(podText),
            "Gross Weight (kg)",
            grossWeightText,
          ],
          [
            "Delivery Address",
            safeText(deliveryAddressText),
            "Transit Time (day)",
            safeText(oceanTransitTime),
          ],
        ];
      } else {
        oceanDetailRows = [
          [
            "POL",
            safeText(polText),
            "Container Type",
            safeText(oceanContainerType),
          ],
          [
            "POD",
            safeText(podText),
            "Quote Type",
            safeText(quoteTypeText),
          ],
          [
            "Gross Weight (kg)",
            grossWeightText,
            "Transit Time (day)",
            safeText(oceanTransitTime),
          ],
        ];
      }

      autoTableWithBg({
        startY: currentY,
        minimumRequiredHeight: 55,

        head: [
          [
            {
              content:
                  "Ocean Transport Details",
              colSpan: 4,
            },
          ],
        ],

        body: oceanDetailRows,
        theme: "grid",

        styles: {
          fontSize: 8,
          cellPadding: 4,
          lineWidth: 0.7,
          valign: "middle",
        },

        headStyles: {
          fillColor: [16, 28, 54],
          textColor: [255, 255, 255],
          halign: "center",
        },

        bodyStyles: {
          halign: "left",
          valign: "middle",
        },

        columnStyles: {
          0: {
            fontStyle: "bold",
            cellWidth: 38,
          },
          1: {
            cellWidth: 72,
          },
          2: {
            fontStyle: "bold",
            cellWidth: 38,
          },
          3: {
            cellWidth: "auto",
          },
        },
      });

      currentY =
          getNextTableStartY(5, 40);

      /* ---------------- OCEAN CHARGES (FCL multi-carrier format) ---------------- */

      const NAVY = [16, 28, 54];
      const LABEL_FILL = [241, 244, 249];
      const ALLIN_FILL = [232, 240, 254];

      const pricingMode = String(
          quotingToolData.oceanPricingMode ||
          "ALL_IN"
      )
          .trim()
          .toUpperCase();

      const carrierRowsSource =
          carriers.length > 0
              ? carriers
              : [
                {
                  name: "Ocean Offer",
                  chargeType: "Ocean Freight Fee",
                  sellingCarriers:
                      quotingToolData.sallingRate ||
                      quotingToolData.sellingRate ||
                      0,
                  sellingCarriersRate:
                      quotingToolData.currency ||
                      "USD",
                },
              ];

      // One comparison column per shipping line; a line may have several
      // charge rows (e.g. Pre Carriage + Ocean Freight Fee).
      const carrierGroups = [];
      carrierRowsSource.forEach((row, index) => {
        const name = safeText(row.name || `Option ${index + 1}`);
        let group = carrierGroups.find((g) => g.name === name);
        if (!group) {
          group = { name, rows: [] };
          carrierGroups.push(group);
        }
        group.rows.push(row);
      });

      // All amounts in the comparison are shown in one currency
      const offerCurrency = normalizeCurrency(
          carrierRowsSource[0]?.sellingCarriersRate,
          quotingToolData.sellingCurrency ||
          quotingToolData.currency ||
          "USD"
      );

      const fmt = (value) =>
          value === null || value === undefined
              ? "N/A"
              : formatAmountMaxTwo(value);

      // Amount with its currency, e.g. "862 EUR"
      const fmtCur = (value) => `${fmt(value)} ${offerCurrency}`;

      const toOffer = (amount, currency) =>
          convertOceanAmount(
              amount,
              normalizeCurrency(currency, offerCurrency),
              offerCurrency
          );

      const sumOffer = (items) =>
          items.reduce(
              (total, item) =>
                  total === null || item === null
                      ? null
                      : total + Number(item || 0),
              0
          );

      /* ---- Origin / destination local charges ---- */

      const pickupService = quotingToolData.pickupService || {};
      const deliveryService = quotingToolData.deliveryService || {};

      const DESTINATION_PATTERN =
          /destination|import|d\/o|delivery order|deconsolidation|last-mile|demurrage|detention|on-carriage|final delivery/i;
      const isImportQuote = /import/i.test(String(quoteType || ""));
      const isDestinationExtra = (type) =>
          DESTINATION_PATTERN.test(String(type || "")) ||
          (isImportQuote && !/origin|export|pre-carriage/i.test(String(type || "")));

      const originLines = [];
      const destinationLines = [];

      if (
          showPickupAddress &&
          !pickupService.sellingIncluded &&
          toNumber(pickupService.selling) > 0
      ) {
        originLines.push({
          label: "Pre-Carriage / Pickup",
          amount: toNumber(pickupService.selling),
          currency: normalizeCurrency(pickupService.sellingCurrency, offerCurrency),
          remarks: pickupService.remarks || (pickupAddressText !== "N/A" ? pickupAddressText : ""),
        });
      }

      if (
          showDeliveryAddress &&
          !deliveryService.sellingIncluded &&
          toNumber(deliveryService.selling) > 0
      ) {
        destinationLines.push({
          label: "On-Carriage / Final Delivery",
          amount: toNumber(deliveryService.selling),
          currency: normalizeCurrency(deliveryService.sellingCurrency, offerCurrency),
          remarks: deliveryService.remarks || (deliveryAddressText !== "N/A" ? deliveryAddressText : ""),
        });
      }

      extraCosts.forEach((extra) => {
        const amount = toNumber(extra.extraSelling);
        if (!amount) return;
        const line = {
          label: extra.type || "Extra Charge",
          amount,
          currency: normalizeCurrency(extra.extraSellingRate, offerCurrency),
          remarks: extra.remarks || "",
        };
        (isDestinationExtra(extra.type) ? destinationLines : originLines).push(line);
      });

      const localTotal = (lines) =>
          sumOffer(lines.map((line) => toOffer(line.amount, line.currency)));
      const originTotal = localTotal(originLines);
      const destinationTotal = localTotal(destinationLines);

      /* ---- Per-carrier ocean freight ---- */

      const chargeTypes = [
        ...new Set(
            carrierRowsSource.map((row) =>
                safeText(row.chargeType || "Ocean Freight Fee")
            )
        ),
      ];

      const carrierAmount = (group, chargeType) => {
        const rows = group.rows.filter(
            (row) => safeText(row.chargeType || "Ocean Freight Fee") === chargeType
        );
        if (!rows.length) return undefined;
        return sumOffer(
            rows.map((row) => toOffer(row.sellingCarriers, row.sellingCarriersRate))
        );
      };

      const carrierFirst = (group, key) =>
          safeText(
              pickFirstValue(...group.rows.map((row) => row[key]))
          );

      const oceanTotals = carrierGroups.map((group) =>
          sumOffer(
              chargeTypes.map((type) => {
                const value = carrierAmount(group, type);
                return value === undefined ? 0 : value;
              })
          )
      );

      const allInTotals = oceanTotals.map((oceanTotal) =>
          sumOffer([originTotal, oceanTotal, destinationTotal])
      );

      // Lowest all-in option is highlighted for the customer
      const validTotals = allInTotals.filter((v) => v !== null);
      const bestTotal = validTotals.length ? Math.min(...validTotals) : null;

      const equipmentFor = (group) => {
        const type = carrierFirst(group, "containerTypes");
        const pcs = toNumber(pickFirstValue(...group.rows.map((row) => row.containerPcs)));
        if (type === "N/A") return "N/A";
        return pcs ? `${pcs} x ${type}` : type;
      };

      const daysText = (value) => {
        const text = String(value ?? "").trim() ? safeText(value) : "N/A";
        if (text === "N/A" || text === "") return "N/A";
        return /day/i.test(text) ? text : `${text} days`;
      };

      const comparisonColumns = carrierGroups.map((group) => group.name);
      const columnCount = comparisonColumns.length + 1;

      const comparisonStyles = {
        theme: "grid",
        styles: { fontSize: 8, cellPadding: 3, lineWidth: 0.4, lineColor: [200, 205, 215] },
        headStyles: { fillColor: NAVY, textColor: [255, 255, 255], halign: "center" },
        columnStyles: {
          0: { fontStyle: "bold", fillColor: LABEL_FILL, cellWidth: 58 },
          // Carrier columns share the remaining width equally
          ...Object.fromEntries(
              comparisonColumns.map((_, index) => [index + 1, { cellWidth: 124 / Math.max(comparisonColumns.length, 1) }])
          ),
        },
      };

      // Small tables move to the next page as a whole instead of splitting
      const heightFor = (rowCount) => 22 + rowCount * 9;

      const rightAlignNumbers = (data) => {
        if (data.section === "body" && data.column.index > 0) {
          data.cell.styles.halign = "right";
        }
      };

      const sectionHead = (title) => [
        [{ content: safeText(title), colSpan: columnCount, styles: { halign: "left" } }],
        ["Description", ...comparisonColumns],
      ];

      const localChargesTable = (number, title, location, lines, total) => {
        if (!lines.length) return;
        autoTableWithBg({
          startY: getNextTableStartY(5, heightFor(lines.length + 1)),
          minimumRequiredHeight: heightFor(lines.length + 1),
          head: [
            [{ content: `${number}   ${title}${location && location !== "N/A" ? `  |  ${location}` : ""}`, colSpan: 4, styles: { halign: "left" } }],
            ["Description", "Currency", "Amount", "Remarks"],
          ],
          body: [
            ...lines.map((line) => [
              safeText(line.label),
              line.currency,
              { content: fmt(line.amount), styles: { halign: "right" } },
              safeText(line.remarks || ""),
            ]),
            [
              { content: `TOTAL ${title}`, styles: { fontStyle: "bold" } },
              { content: offerCurrency, styles: { fontStyle: "bold" } },
              { content: fmt(total), styles: { fontStyle: "bold", halign: "right" } },
              "",
            ],
          ],
          ...comparisonStyles,
          columnStyles: {
            0: { fontStyle: "bold", fillColor: LABEL_FILL, cellWidth: 62 },
            1: { cellWidth: 22, halign: "center" },
            2: { cellWidth: 30 },
            3: { cellWidth: "auto" },
          },
        });
      };

      const transitRow = [
        "Estimated Transit Time",
        ...carrierGroups.map((group) =>
            daysText(pickFirstValue(...group.rows.map((row) => row.transitTime), quotingToolData.transitTime))
        ),
      ];
      const originFreeRow = [
        "Origin Free Time",
        ...carrierGroups.map((group) =>
            daysText(pickFirstValue(...group.rows.map((row) => row.originFreeTime), quotingToolData.originFreeTime))
        ),
      ];
      const destinationFreeRow = [
        "Destination Free Time",
        ...carrierGroups.map(() => daysText(quotingToolData.destinationFreeTime)),
      ];
      const equipmentRow = ["Equipment", ...carrierGroups.map(equipmentFor)];

      const highlightBest = (data) => {
        rightAlignNumbers(data);
        if (data.section !== "body" || data.column.index === 0) return;
        const isTotalRow = data.row.raw?.[0]?.content === "ESTIMATED ALL-IN" || data.row.raw?.[0] === "ESTIMATED ALL-IN";
        if (isTotalRow) {
          data.cell.styles.fontStyle = "bold";
          data.cell.styles.fillColor = ALLIN_FILL;
          if (bestTotal !== null && allInTotals[data.column.index - 1] === bestTotal && validTotals.length > 1) {
            data.cell.styles.fillColor = [220, 252, 231];
            data.cell.styles.textColor = [21, 128, 61];
          }
        }
      };

      if (pricingMode === "ALL_IN") {
        /*
         * ALL-IN: only the total per carrier plus the service terms.
         */
        autoTableWithBg({
          startY: getNextTableStartY(5, heightFor(5)),
          minimumRequiredHeight: heightFor(5),
          head: sectionHead(`ESTIMATED ALL-IN RATE  |  ${offerCurrency} / SHIPMENT`),
          body: [
            [{ content: "ESTIMATED ALL-IN", styles: { fontStyle: "bold" } }, ...allInTotals.map(fmtCur)],
            equipmentRow,
            transitRow,
            originFreeRow,
            destinationFreeRow,
          ],
          ...comparisonStyles,
          didParseCell: highlightBest,
        });
      } else {
        /*
         * BREAKDOWN: 01 origin, 02 destination, 03 ocean freight per
         * carrier, 04 all-in comparison (origin + ocean + destination).
         */
        localChargesTable("01", "ORIGIN CHARGES", safeText(firstCarrier.pol), originLines, originTotal);
        localChargesTable("02", "DESTINATION CHARGES", safeText(firstCarrier.pod), destinationLines, destinationTotal);

        autoTableWithBg({
          startY: getNextTableStartY(5, heightFor(chargeTypes.length + 5)),
          minimumRequiredHeight: heightFor(chargeTypes.length + 5),
          head: sectionHead(`03   ${carrierGroups.length > 1 ? "MULTI-CARRIER " : ""}OCEAN FREIGHT  |  ${offerCurrency}`),
          body: [
            ...chargeTypes.map((type) => [
              type,
              ...carrierGroups.map((group) => {
                const value = carrierAmount(group, type);
                return value === undefined ? "-" : fmtCur(value);
              }),
            ]),
            [{ content: `Ocean Total (${offerCurrency})`, styles: { fontStyle: "bold" } }, ...oceanTotals.map((v) => ({ content: fmtCur(v), styles: { fontStyle: "bold" } }))],
            equipmentRow,
            transitRow,
            originFreeRow,
            destinationFreeRow,
          ],
          ...comparisonStyles,
          didParseCell: rightAlignNumbers,
        });

        autoTableWithBg({
          startY: getNextTableStartY(5, heightFor(4)),
          minimumRequiredHeight: heightFor(4),
          head: sectionHead(`04   ESTIMATED ALL-IN COMPARISON  |  ${offerCurrency} / SHIPMENT`),
          body: [
            ["Origin Charges", ...carrierGroups.map(() => fmtCur(originTotal))],
            ["Ocean Freight", ...oceanTotals.map(fmtCur)],
            ["Destination Charges", ...carrierGroups.map(() => fmtCur(destinationTotal))],
            [{ content: "ESTIMATED ALL-IN", styles: { fontStyle: "bold" } }, ...allInTotals.map(fmtCur)],
          ],
          ...comparisonStyles,
          didParseCell: highlightBest,
        });
      }

      /* ---- Important note ---- */
      autoTableWithBg({
        startY: getNextTableStartY(5, 25),
        minimumRequiredHeight: 25,
        body: [[
          "IMPORTANT: Rates are estimated selling rates in " + offerCurrency + ". " +
          "The all-in covers only the listed services and excludes duties, taxes, storage, demurrage/detention and unconfirmed items. " +
          "Rates, space, equipment, routing, free time and carrier acceptance are subject to confirmation at booking." +
          (bestTotal !== null && validTotals.length > 1 ? " The most economical option is highlighted in green." : ""),
        ]],
        theme: "plain",
        styles: { fontSize: 7.5, cellPadding: 3, fillColor: [255, 247, 237], textColor: [124, 45, 18] },
      });

      /* ---- Customer acceptance ---- */
      autoTableWithBg({
        startY: getNextTableStartY(6, 52),
        minimumRequiredHeight: 52,
        head: [
          [{ content: "CUSTOMER ACCEPTANCE", colSpan: 2, styles: { halign: "left" } }],
          ["SELECTED CARRIER / OPTION", "AUTHORIZED ACCEPTANCE"],
        ],
        body: [
          [
            `Carrier: ${carrierGroups.length > 1 ? comparisonColumns.join(" / ") + "  (circle one)" : comparisonColumns[0] || "________________"}`,
            "Name / Title: ______________________________",
          ],
          [
            `Quotation reference: ${safeText(quotingToolData.quoteNo || "N/A")}`,
            "Signature / Date: ___________________________",
          ],
        ],
        theme: "grid",
        styles: { fontSize: 8, cellPadding: 4, lineWidth: 0.4, lineColor: [200, 205, 215], minCellHeight: 10 },
        headStyles: { fillColor: NAVY, textColor: [255, 255, 255] },
        columnStyles: { 0: { cellWidth: 91 } },
      });
    }

    else if (selectedTransportType === "Air") {
      let currentY = getNextTableStartY(5, 55);

      const carriers = Array.isArray(quotingToolData.carriers)
          ? quotingToolData.carriers
          : [];

      const extraCosts = Array.isArray(quotingToolData.extraCosts)
          ? quotingToolData.extraCosts
          : [];

      const firstCarrier = carriers[0] || {};

      const normalizeAirCurrency = (
          value,
          fallback = "USD"
      ) =>
          String(value || fallback || "USD")
              .trim()
              .toUpperCase();

      const joinParts = (...parts) =>
          parts
              .map((value) => safeText(value))
              .filter(
                  (value) =>
                      value &&
                      value !== "N/A"
              )
              .join(" / ");

      const normalizeServiceName = (service) =>
          String(service || "Port to Port")
              .trim()
              .replace(/\s+/g, " ");

      const normalizedService =
          normalizeServiceName(
              quotingToolData.service
          );

      const showPickupAddress = [
        "Door to Port",
        "Door to Door",
      ].includes(normalizedService);

      const showDeliveryAddress = [
        "Port to Door",
        "Door to Door",
      ].includes(normalizedService);

      const formatAirAddress = (
          structuredAddress,
          stringAddress = ""
      ) => {
        if (
            structuredAddress &&
            typeof structuredAddress === "object" &&
            !Array.isArray(structuredAddress)
        ) {
          const country = safeText(
              structuredAddress.country || ""
          );

          const city = safeText(
              structuredAddress.city || ""
          );

          const additionalInfo = safeText(
              structuredAddress.additionalInfo ||
              structuredAddress.fullAddress ||
              ""
          );

          const addressText = joinParts(
              country,
              city,
              additionalInfo
          );

          if (addressText) {
            return addressText;
          }
        }

        const fallbackAddress = String(
            stringAddress || ""
        ).trim();

        return fallbackAddress
            ? safeText(fallbackAddress)
            : "N/A";
      };

      const pickupAddressText =
          formatAirAddress(
              quotingToolData.pickupAddress1 ||
              quotingToolData.pickupAddressDetail,
              quotingToolData.pickupAddress
          );

      const deliveryAddressText =
          formatAirAddress(
              quotingToolData.deliveryAddress2 ||
              quotingToolData.deliveryAddressDetail,
              quotingToolData.deliveryAddress
          );

      const departureAirportText =
          firstCarrier.polCountry ||
          firstCarrier.pol
              ? joinParts(
                  firstCarrier.polCountry,
                  firstCarrier.pol
              )
              : safeText(
                  quotingToolData.originAirport ||
                  "N/A"
              );

      const arrivalAirportText =
          firstCarrier.podCountry ||
          firstCarrier.pod
              ? joinParts(
                  firstCarrier.podCountry,
                  firstCarrier.pod
              )
              : safeText(
                  quotingToolData.destinationAirport ||
                  "N/A"
              );

      const quoteTypeText =
          [
            quotingToolData.quoteType,
            quotingToolData.airTransportType,
          ]
              .map((value) =>
                  String(value || "").trim()
              )
              .filter(Boolean)
              .join(", ") || "N/A";

      const grossWeightText = safeText(
          quotingToolData.grossWeight ||
          "N/A"
      );

      const transitTimeText = safeText(
          firstCarrier.transitTime ||
          quotingToolData.transitTime ||
          "N/A"
      );

      /* ---------------- AIR TRANSPORT DETAILS ---------------- */

      let airDetailRows = [];

      if (
          normalizedService === "Port to Port"
      ) {
        airDetailRows = [
          [
            "Airport of Departure",
            safeText(departureAirportText),
            "Service",
            safeText(normalizedService),
          ],
          [
            "Airport of Arrival",
            safeText(arrivalAirportText),
            "Quote Type",
            safeText(quoteTypeText),
          ],
          [
            "Gross Weight (kg)",
            grossWeightText,
            "Transit Time (day)",
            transitTimeText,
          ],
        ];
      } else if (
          normalizedService === "Door to Port"
      ) {
        airDetailRows = [
          [
            "Pickup Address",
            safeText(pickupAddressText),
            "Service",
            safeText(normalizedService),
          ],
          [
            "Airport of Departure",
            safeText(departureAirportText),
            "Quote Type",
            safeText(quoteTypeText),
          ],
          [
            "Airport of Arrival",
            safeText(arrivalAirportText),
            "Gross Weight (kg)",
            grossWeightText,
          ],
          [
            "Transit Time (day)",
            transitTimeText,
            "",
            "",
          ],
        ];
      } else if (
          normalizedService === "Port to Door"
      ) {
        airDetailRows = [
          [
            "Airport of Departure",
            safeText(departureAirportText),
            "Service",
            safeText(normalizedService),
          ],
          [
            "Airport of Arrival",
            safeText(arrivalAirportText),
            "Quote Type",
            safeText(quoteTypeText),
          ],
          [
            "Delivery Address",
            safeText(deliveryAddressText),
            "Gross Weight (kg)",
            grossWeightText,
          ],
          [
            "Transit Time (day)",
            transitTimeText,
            "",
            "",
          ],
        ];
      } else if (
          normalizedService === "Door to Door"
      ) {
        airDetailRows = [
          [
            "Pickup Address",
            safeText(pickupAddressText),
            "Service",
            safeText(normalizedService),
          ],
          [
            "Airport of Departure",
            safeText(departureAirportText),
            "Quote Type",
            safeText(quoteTypeText),
          ],
          [
            "Airport of Arrival",
            safeText(arrivalAirportText),
            "Gross Weight (kg)",
            grossWeightText,
          ],
          [
            "Delivery Address",
            safeText(deliveryAddressText),
            "Transit Time (day)",
            transitTimeText,
          ],
        ];
      } else {
        airDetailRows = [
          [
            "Airport of Departure",
            safeText(departureAirportText),
            "Service",
            safeText(
                normalizedService ||
                "Port to Port"
            ),
          ],
          [
            "Airport of Arrival",
            safeText(arrivalAirportText),
            "Quote Type",
            safeText(quoteTypeText),
          ],
          [
            "Gross Weight (kg)",
            grossWeightText,
            "Transit Time (day)",
            transitTimeText,
          ],
        ];
      }

      autoTableWithBg({
        startY: currentY,
        minimumRequiredHeight: 55,

        head: [
          [
            {
              content:
                  "Air Transport Details",
              colSpan: 4,
            },
          ],
        ],

        body: airDetailRows,
        theme: "grid",

        styles: {
          fontSize: 8,
          cellPadding: 4,
          lineWidth: 0.7,
          valign: "middle",
        },

        headStyles: {
          fillColor: [16, 28, 54],
          textColor: [255, 255, 255],
          halign: "center",
        },

        bodyStyles: {
          halign: "left",
          valign: "middle",
        },

        columnStyles: {
          0: {
            fontStyle: "bold",
            cellWidth: 45,
          },
          1: {
            cellWidth: 65,
          },
          2: {
            fontStyle: "bold",
            cellWidth: 40,
          },
          3: {
            cellWidth: "auto",
          },
        },
      });

      currentY =
          getNextTableStartY(5, 40);

      /* ---------------- AIR CHARGES DETAILS ---------------- */

      const pricingMode = String(
          quotingToolData.airPricingMode ||
          "ALL_IN"
      )
          .trim()
          .toUpperCase();

      /*
       * Exchange rate bilgileri yoksa
       * PDF hazırlanırken bir kez alınır.
       */
      let airExchangeRates =
          tableExchangeRates;

      if (!airExchangeRates) {
        try {
          const exchangeResponse = await fetch(
              "https://api.exchangerate-api.com/v4/latest/USD"
          );

          if (!exchangeResponse.ok) {
            throw new Error(
                `Exchange rate request failed: ${exchangeResponse.status}`
            );
          }

          const exchangeData =
              await exchangeResponse.json();

          airExchangeRates =
              exchangeData?.rates &&
              typeof exchangeData.rates === "object"
                  ? exchangeData.rates
                  : null;
        } catch (error) {
          console.error(
              "Air PDF exchange rates could not be loaded:",
              error
          );
        }
      }

      /*
       * Source Currency -> USD -> Target Currency
       */
      const convertAirAmount = (
          amount,
          fromCurrency,
          toCurrency
      ) => {
        const numericAmount =
            toNumber(amount);

        const from = normalizeAirCurrency(
            fromCurrency,
            toCurrency || "USD"
        );

        const to = normalizeAirCurrency(
            toCurrency,
            fromCurrency || "USD"
        );

        if (numericAmount === 0) {
          return 0;
        }

        if (from === to) {
          return numericAmount;
        }

        const fromRate =
            from === "USD"
                ? 1
                : Number(
                    airExchangeRates?.[from]
                );

        const toRate =
            to === "USD"
                ? 1
                : Number(
                    airExchangeRates?.[to]
                );

        if (
            !Number.isFinite(fromRate) ||
            fromRate <= 0 ||
            !Number.isFinite(toRate) ||
            toRate <= 0
        ) {
          console.error(
              `Air currency conversion failed: ${from} -> ${to}`
          );

          return null;
        }

        const amountInUSD =
            numericAmount / fromRate;

        return amountInUSD * toRate;
      };

      const carrierOptions =
          carriers.length > 0
              ? carriers
              : [
                {
                  name: "Air Offer",
                  chargeType:
                      "Air Freight Charge",
                  sellingCarriers:
                      quotingToolData.sallingRate ||
                      quotingToolData.sellingRate ||
                      0,
                  sellingCarriersRate:
                      quotingToolData.currency ||
                      "USD",
                },
              ];

      const pickupService =
          quotingToolData.pickupService || {};

      const deliveryService =
          quotingToolData.deliveryService || {};

      carrierOptions.forEach(
          (carrierItem, carrierIndex) => {
            const carrierName = safeText(
                carrierItem.name ||
                `Carrier #${carrierIndex + 1}`
            );

            /*
             * Air Freight Selling para birimi:
             * Carrier Selling yanında seçilen currency.
             */
            const offerCurrency =
                normalizeAirCurrency(
                    carrierItem.sellingCarriersRate,
                    quotingToolData.sellingCurrency ||
                    quotingToolData.currency ||
                    "USD"
                );

            const carrierSellingAmount =
                toNumber(
                    carrierItem.sellingCarriers
                );

            const carrierSellingCurrency =
                normalizeAirCurrency(
                    carrierItem.sellingCarriersRate,
                    offerCurrency
                );

            const convertedCarrierSelling =
                convertAirAmount(
                    carrierSellingAmount,
                    carrierSellingCurrency,
                    offerCurrency
                );

            /*
             * Pickup selling ayrı görünmez.
             * Air Freight Charge içine eklenir.
             */
            const pickupSellingIncluded =
                Boolean(
                    pickupService.sellingIncluded
                );

            const pickupSellingAmount =
                showPickupAddress &&
                !pickupSellingIncluded
                    ? toNumber(
                        pickupService.selling
                    )
                    : 0;

            const pickupSellingCurrency =
                normalizeAirCurrency(
                    pickupService.sellingCurrency,
                    offerCurrency
                );

            const convertedPickupSelling =
                pickupSellingAmount === 0
                    ? 0
                    : convertAirAmount(
                        pickupSellingAmount,
                        pickupSellingCurrency,
                        offerCurrency
                    );

            /*
             * Delivery selling ayrı görünmez.
             * Air Freight Charge içine eklenir.
             */
            const deliverySellingIncluded =
                Boolean(
                    deliveryService.sellingIncluded
                );

            const deliverySellingAmount =
                showDeliveryAddress &&
                !deliverySellingIncluded
                    ? toNumber(
                        deliveryService.selling
                    )
                    : 0;

            const deliverySellingCurrency =
                normalizeAirCurrency(
                    deliveryService.sellingCurrency,
                    offerCurrency
                );

            const convertedDeliverySelling =
                deliverySellingAmount === 0
                    ? 0
                    : convertAirAmount(
                        deliverySellingAmount,
                        deliverySellingCurrency,
                        offerCurrency
                    );

            /*
             * Air Freight Charge toplamı:
             *
             * Carrier Selling
             * + Pickup Selling
             * + Delivery Selling
             */
            const airFreightConversionError =
                convertedCarrierSelling === null ||
                convertedPickupSelling === null ||
                convertedDeliverySelling === null;

            const combinedAirFreightSelling =
                airFreightConversionError
                    ? null
                    : Number(
                        convertedCarrierSelling || 0
                    ) +
                    Number(
                        convertedPickupSelling || 0
                    ) +
                    Number(
                        convertedDeliverySelling || 0
                    );

            /*
             * Extra Selling:
             * Breakdown içinde kendi seçilen currency'siyle görünür.
             * Offer hesabı için offerCurrency'ye çevrilir.
             */
            const preparedExtraCosts =
                extraCosts.map((extraItem) => {
                  const originalAmount =
                      toNumber(
                          extraItem.extraSelling
                      );

                  const originalCurrency =
                      normalizeAirCurrency(
                          extraItem.extraSellingRate,
                          offerCurrency
                      );

                  const convertedAmount =
                      convertAirAmount(
                          originalAmount,
                          originalCurrency,
                          offerCurrency
                      );

                  return {
                    ...extraItem,
                    originalAmount,
                    originalCurrency,
                    convertedAmount,
                  };
                });

            const extraRows =
                preparedExtraCosts.map(
                    (extraItem) => [
                      safeText(
                          `Extra Charge (${safeText(
                              extraItem.type || "Extra"
                          )})`
                      ),
                      {
                        content: safeText(
                            `${formatAmountMaxTwo(
                                extraItem.originalAmount
                            )} ${
                                extraItem.originalCurrency
                            }`
                        ),
                        styles: {
                          halign: "right",
                        },
                      },
                    ]
                );

            const extraConversionError =
                preparedExtraCosts.some(
                    (extraItem) =>
                        extraItem.convertedAmount ===
                        null
                );

            const hasConversionError =
                airFreightConversionError ||
                extraConversionError;

            const convertedExtraTotal =
                hasConversionError
                    ? null
                    : preparedExtraCosts.reduce(
                        (total, extraItem) =>
                            total +
                            Number(
                                extraItem.convertedAmount ||
                                0
                            ),
                        0
                    );

            const calculatedOfferTotal =
                hasConversionError
                    ? null
                    : Number(
                        combinedAirFreightSelling ||
                        0
                    ) +
                    Number(
                        convertedExtraTotal || 0
                    );

            /*
             * ALL-IN kayıtlarda manuel/genel selling varsa
             * onu kullanırız.
             *
             * BREAKDOWN'da satırlardan hesaplanan toplam kullanılır.
             */
            const savedTotalSelling =
                toNumber(
                    quotingToolData.sallingRate ||
                    quotingToolData.sellingRate ||
                    0
                );

            const offerTotal =
                pricingMode === "ALL_IN" &&
                savedTotalSelling > 0
                    ? convertAirAmount(
                        savedTotalSelling,
                        quotingToolData.currency ||
                        offerCurrency,
                        offerCurrency
                    )
                    : calculatedOfferTotal;

            const airFreightAmountText =
                combinedAirFreightSelling ===
                null
                    ? `N/A ${offerCurrency}`
                    : `${formatAmountMaxTwo(
                        combinedAirFreightSelling
                    )} ${offerCurrency}`;

            const offerAmountText =
                offerTotal === null
                    ? `N/A ${offerCurrency}`
                    : `${formatAmountMaxTwo(
                        offerTotal
                    )} ${offerCurrency}`;

            let feeRows = [];

            if (pricingMode === "ALL_IN") {
              feeRows = [
                [
                  {
                    content:
                        "Estimated All-in Air Rate",
                    styles: {
                      fontStyle: "bold",
                    },
                  },
                  {
                    content: safeText(
                        offerAmountText
                    ),
                    styles: {
                      fontStyle: "bold",
                      halign: "right",
                    },
                  },
                ],
              ];
            } else {
              /*
               * Pickup / Delivery ayrı görünmez.
               * Air Freight Charge içine dahildir.
               */
              feeRows = [
                [
                  safeText(
                      carrierItem.chargeType ||
                      "Air Freight Charge"
                  ),
                  {
                    content: safeText(
                        airFreightAmountText
                    ),
                    styles: {
                      halign: "right",
                    },
                  },
                ],

                ...extraRows,

                [
                  {
                    content: "Offer",
                    styles: {
                      fontStyle: "bold",
                    },
                  },
                  {
                    content: safeText(
                        offerAmountText
                    ),
                    styles: {
                      fontStyle: "bold",
                      halign: "right",
                    },
                  },
                ],
              ];
            }

            autoTableWithBg({
              startY: currentY,
              minimumRequiredHeight: 40,

              head: [
                [
                  {
                    content:
                        carriers.length > 1
                            ? `Charges Details - ${carrierName}`
                            : "Charges Details",
                    colSpan: 2,
                  },
                ],
              ],

              body: feeRows,
              theme: "grid",

              styles: {
                fontSize: 8,
                cellPadding: 4,
                lineWidth: 0.7,
              },

              headStyles: {
                fillColor: [16, 28, 54],
                textColor: [
                  255,
                  255,
                  255,
                ],
                halign: "center",
              },

              bodyStyles: {
                halign: "left",
                valign: "middle",
              },

              columnStyles: {
                0: {
                  cellWidth: 110,
                },
                1: {
                  cellWidth: "auto",
                  halign: "right",
                },
              },
            });

            currentY =
                getNextTableStartY(5, 40);
          }
      );
    }
    else if (selectedTransportType === "Multimodal") {
      const multimodalFee = `${safeText(formatAmountMaxTwo(quotingToolData?.sallingRate))} ${safeText(
          quotingToolData?.currency || ""
      )}`.trim();

      offerDetails = [
        [
          "Origin", "Destination", "Cargo Type", "Gross Weight (kg)", "Transit Time (day)", "Service", "Multimodal Freight Fee",
        ],
        [
          safeText(quotingToolData?.pickupAddress), safeText(quotingToolData?.deliveryAddress), safeText(quotingToolData?.packing), safeText(quotingToolData?.grossWeight), safeText(quotingToolData?.transitTime), safeText(quotingToolData?.service), multimodalFee,
        ],
      ];
    }
    else if (selectedTransportType === "ProjectCargo") {
      let currentY = (doc.lastAutoTable?.finalY || 55) + 5;

      const pickup = `${safeText(quotingToolData?.pickupLocation?.country || "")}${
          quotingToolData?.pickupLocation?.city
              ? " / " + safeText(quotingToolData.pickupLocation.city)
              : ""
      }`.trim() || "N/A";

      const delivery = `${safeText(quotingToolData?.deliveryLocation?.country || "")}${
          quotingToolData?.deliveryLocation?.city
              ? " / " + safeText(quotingToolData.deliveryLocation.city)
              : ""
      }`.trim() || "N/A";

      const exitCustoms = `${safeText(quotingToolData?.exportLocation?.country || "")}${
          quotingToolData?.exportLocation?.additionalInfo
              ? " / " + safeText(quotingToolData.exportLocation.additionalInfo)
              : ""
      }`.trim() || "N/A";

      const arrivalCustoms = `${safeText(quotingToolData?.importLocation?.country || "")}${
          quotingToolData?.importLocation?.additionalInfo
              ? " / " + safeText(quotingToolData.importLocation.additionalInfo)
              : ""
      }`.trim() || "N/A";

      /* ---------------- PROJECT CARGO DETAILS ---------------- */

      autoTableWithBg({
        startY: currentY,
        head: [[{ content: "Project Cargo Transport Details", colSpan: 4 }]],
        body: [
          ["Pickup Address", pickup, "Delivery Address", delivery],
          ["Exit Customs", exitCustoms, "Arrival Customs", arrivalCustoms],
          ["Service", safeText(quotingToolData?.service || "N/A"),
            "Commodity", safeText(quotingToolData?.commodity || "N/A")],
          ["Transit Time (day)",
            safeText(quotingToolData.transitTime || "N/A"),
            "Quote Type",
            safeText(
                `${safeText(quotingToolData.quoteType || "")}, ${safeText(
                    quotingToolData.modeType || ""
                )}`
            ),
          ],
          ["Gross Weight (kg)", safeText(quotingToolData?.grossWeight || "N/A"),
            "Chargeable Weight (kg)", safeText(quotingToolData?.chargeableWeight || "N/A")]],

        theme: "grid",
        styles: { fontSize: 8, cellPadding: 4, lineWidth: 0.7 },
        headStyles: {
          fillColor: [16, 28, 54],
          textColor: [255, 255, 255],
          halign: "center",
        },
        columnStyles: {
          0: { fontStyle: "bold", cellWidth: 45 },
          1: { cellWidth: 55 },
          2: { fontStyle: "bold", cellWidth: 45 },
          3: { cellWidth: "auto" },
        },
      });

      currentY = getNextTableStartY(5, 40);

      /* ---------------- CHARGES (ALL-IN / BREAKDOWN) ---------------- */

      const pricingMode = quotingToolData?.airPricingMode || "ALL_IN";
      const sellingCurrency = safeText(quotingToolData?.currency || "EUR");

      const carriers = Array.isArray(quotingToolData?.carriers)
          ? quotingToolData.carriers
          : [];

      const extraCosts = Array.isArray(quotingToolData?.extraCosts)
          ? quotingToolData.extraCosts
          : [];

      let feeRows = [];

      if (pricingMode === "ALL_IN") {
        feeRows = [
          [
            { content: "Estimated Project Cargo Rate (All-in)", styles: { fontStyle: "bold" } },
            {
              content: safeText(
                  `${formatAmountMaxTwo(quotingToolData?.sallingRate || 0)} ${sellingCurrency}`
              ),
              styles: { fontStyle: "bold", halign: "right" },
            },
          ],
        ];
      } else {
        const carrierRows = carriers.map((c) => [
          safeText(c?.name || "Carrier Cost"),
          safeText(
              `${formatAmountMaxTwo(c?.sellingCarriers || 0)} ${sellingCurrency}`
          ),
        ]);

        const extraRows = extraCosts.map((e) => [
          safeText(`Extra Charge (${safeText(e?.type || "Extra")})`),
          safeText(
              `${formatAmountMaxTwo(e?.extraSelling || 0)} ${sellingCurrency}`
          ),
        ]);

        feeRows = [
          ...carrierRows,
          ...extraRows,
          [
            { content: "Total Offer", styles: { fontStyle: "bold" } },
            {
              content: safeText(
                  `${formatAmountMaxTwo(quotingToolData?.sallingRate || 0)} ${sellingCurrency}`
              ),
              styles: { fontStyle: "bold", halign: "right" },
            },
          ],
        ];
      }

      autoTableWithBg({
        startY: currentY,
        head: [[{ content: "Charges Details", colSpan: 2 }]],
        body: feeRows,
        theme: "grid",
        styles: { fontSize: 8, cellPadding: 4, lineWidth: 0.7 },
        headStyles: {
          fillColor: [16, 28, 54],
          textColor: [255, 255, 255],
          halign: "center",
        },
        bodyStyles: {
          halign: "left",
          valign: "middle",
        },
        columnStyles: {
          0: { cellWidth: 110 },
          1: { cellWidth: "auto", halign: "right" },
        },
      });
    }

    if (Array.isArray(offerDetails) && offerDetails.length > 1) {
      autoTableWithBg({
        startY: getNextTableStartY(8, 40),
        minimumRequiredHeight: 40,
        head: [offerDetails[0]],
        body: offerDetails.slice(1),
        theme: "grid",
        styles: {
          fontSize: 8,
          cellPadding: 4,
          lineWidth: 0.7,
        },
        headStyles: {
          fillColor: [16, 28, 54],
          textColor: [255, 255, 255],
          halign: "center",
          lineWidth: 0.7,
          lineColor: [192, 192, 192],
        },
        bodyStyles: {
          halign: "center",
          valign: "middle",
          fontStyle: "bold",
        },
        columnStyles: {
          0: { halign: "left" },
          1: { halign: "left" },
        },
      });
    }

    const termsStartY = getNextTableStartY(12, 55);

    autoTableWithBg({
      startY: termsStartY,
      minimumRequiredHeight: 55,
      head: [["Terms & Conditions"]],
      body: [[termsAndConditionsText]],
      theme: "grid",
      styles: {
        fontSize: 5.9,
        cellPadding: 5,
        lineWidth: 0.7,
        valign: "top",
      },
      headStyles: {
        fillColor: [16, 28, 54],
        textColor: [255, 255, 255],
        halign: "center",
      },
      bodyStyles: {
        halign: "left",
        valign: "top",
      },
      rowPageBreak: "auto",
      showHead: "everyPage",
    });

    const trToAscii = (input = "") =>
        String(input)
            .replaceAll("İ", "I")
            .replaceAll("İ", "I")
            .replaceAll("ı", "i")
            .replaceAll("Ş", "S")
            .replaceAll("ş", "s")
            .replaceAll("Ğ", "G")
            .replaceAll("ğ", "g")
            .replaceAll("Ü", "U")
            .replaceAll("ü", "u")
            .replaceAll("Ö", "O")
            .replaceAll("ö", "o")
            .replaceAll("Ç", "C")
            .replaceAll("ç", "c");

    // Turkish terms always start on a clean page after English terms.
    doc.addPage();
    addBackgroundToPageOnce();

    autoTableWithBg({
      startY: 30,
      minimumRequiredHeight: 55,
      head: [[trToAscii("Şartlar & Koşullar")]],
      body: [[trToAscii(termsAndConditionsTextTR)]],
      theme: "grid",
      styles: {
        fontSize: 6,
        cellPadding: 5,
        lineWidth: 0.7,
        valign: "top",
      },
      headStyles: {
        fillColor: [16, 28, 54],
        textColor: [255, 255, 255],
        halign: "center",
      },
      bodyStyles: {
        halign: "left",
        valign: "top",
      },
      rowPageBreak: "auto",
      showHead: "everyPage",
    });

    const timestamp = new Date().toISOString().replace(/:/g, "-");
    const pdfFileName = `QuotingTool_${timestamp}.pdf`;
    const pdfBlob = doc.output("blob");
    const pdfUrl = URL.createObjectURL(pdfBlob);
    const newTab = window.open(pdfUrl, "_blank");
    if (newTab) {
      setTimeout(() => {
        newTab.document.title = pdfFileName;
      }, 100);
    }
    // Alıcı: teklifteki adres, yoksa müşteri kartındaki e-posta; hiçbiri yoksa boş (kullanıcı e-posta istemcisinde girer)
    const selectedCustomerEmail =
        quotingToolData.customerEmail ||
        customerEmails[String(quotingToolData.customer || "").trim().toLowerCase()] ||
        "";
    const ccEmails = "pricing@filenders.com";
    const transportType = quotingToolData.selectedTransportType || "Unknown";
    let transportDetails = "";
    if (selectedTransportType === "Road") {
      const pickupLocation = quotingToolData.pickupLocation || {};
      const pickupAddress = `${pickupLocation.additionalInfo || "N/A"}, ${pickupLocation.city || "N/A"}, ${pickupLocation.country || "N/A"}`;

      const deliveryLocation = quotingToolData.deliveryLocation || {};
      const deliveryAddress = `${deliveryLocation.additionalInfo || "N/A"}, ${deliveryLocation.city || "N/A"}, ${deliveryLocation.country || "N/A"}`;

      const exportLocation = quotingToolData.exportLocation || {};
      const exportAddress = `${exportLocation.additionalInfo || "N/A"}, ${exportLocation.country || "N/A"}`;

      const importLocation = quotingToolData.importLocation || {};
      const importAddress = `${importLocation.additionalInfo || "N/A"}, ${importLocation.country || "N/A"}`;

      transportDetails =
          `Pickup Location: ${pickupAddress}
          Export Customs: ${exportAddress}
          Arrival Customs: ${importAddress}
           Delivery Location: ${deliveryAddress}
          `;
    }

    else if (selectedTransportType === "Ocean") {
      transportDetails = `Port of Loading (POL): ${quotingToolData.pol || "N/A"}
Port of Discharge (POD): ${quotingToolData.pod || "N/A"}
Final Destination: ${quotingToolData.finalDestination || "N/A"}`;
    }
    else if (selectedTransportType === "Air") {
      const carrier = (quotingToolData.carriers || [])[0] || {};

      const joinMailParts = (...parts) =>
          parts
              .map((x) => String(x || "").trim())
              .filter(Boolean)
              .join(" / ");

      const originAirport =
          carrier.polCountry || carrier.pol
              ? joinMailParts(carrier.polCountry, carrier.pol)
              : quotingToolData.originAirport || "N/A";

      const destinationAirport =
          carrier.podCountry || carrier.pod
              ? joinMailParts(carrier.podCountry, carrier.pod)
              : quotingToolData.destinationAirport || "N/A";

      const formatAirAddressForMail = (address = {}) => {
        if (!address || typeof address !== "object") return "N/A";

        const type = String(address.type || "").trim();

        const parts =
            type === "door"
                ? [address.country, address.city, address.additionalInfo]
                : [address.country, address.port];

        return joinMailParts(...parts) || "N/A";
      };

      const service = String(quotingToolData.service || "").trim();

      const showPickupAddress = ["Door to Door", "Door to Port"].includes(service);
      const showDeliveryAddress = ["Door to Door", "Port to Door"].includes(service);

      transportDetails = `Origin Airport: ${originAirport}
Destination Airport: ${destinationAirport}
Pickup Address: ${showPickupAddress ? formatAirAddressForMail(quotingToolData.pickupAddress1) : "N/A"}
Delivery Address: ${showDeliveryAddress ? formatAirAddressForMail(quotingToolData.deliveryAddress2) : "N/A"}
Final Destination: ${quotingToolData.finalDestination || "N/A"}`;
    }

    else if (selectedTransportType === "Multimodal") {
      transportDetails = `Pickup Address: ${quotingToolData.pickupAddress || "N/A"}
Delivery Address: ${quotingToolData.deliveryAddress || "N/A"}`;
    } else {
      transportDetails = "Transport details not available.";
    }
    const validityPeriod = formatDate(quotingToolData.estimatedCloseDate);
    const quotationDetails = `
Quotation Number: ${quotingToolData.quoteNo}
Mode of Transport: ${transportType}
Service: ${quotingToolData.service}
${transportDetails}
Validity Period: Until ${validityPeriod}
Transit Time: ${quotingToolData.transitTime} days
Estimated Rate: ${formatAmountMaxTwo(quotingToolData.sallingRate)} ${quotingToolData.currency}
🔗 You can access the full offer details in the attached file.

Please feel free to reach out if you have any questions regarding this offer. We look forward to the opportunity to work with you.
`.replace(/\n/g, "%0D%0A"); // Yeni satırları mail formatına uygun hale getiriyoruz.

    const emailBody = `I hope this email finds you well. As ${quotingToolData.customer}, we are pleased to share our quotation based on your request. Below, you will find a summary of our offer:

${quotationDetails}`.replace(/\n/g, "%0D%0A"); // Satırları düzgün göstermek için

    const emailURL = `mailto:${selectedCustomerEmail}?cc=${ccEmails}&subject= Quote Tool  ${quotingToolData.customer}  ${quotingToolData.quoteNo}  &body=${emailBody}`;
    setTimeout(() => {
      if (newTab) {
        const buttonHtml = `
    <div style="position: fixed; top: 10px; right: 10px; display: flex; gap: 10px;">
      <!-- 📩 Send to Customer Butonu -->
      <button id="sendToCustomerBtn"
        style="
          background-color: orange; 
          color: white; 
          border: none; 
          padding: 10px 15px; 
          font-size: 14px; 
          cursor: pointer;
          border-radius: 5px;
        "
        onclick="window.open('${emailURL}', '_self')"
      >
        📩 Send to Customer
      </button>

      <!-- 📥 Download PDF Butonu -->
      <button id="downloadPdfBtn"
        style="
          background-color: #007bff; 
          color: white; 
          border: none; 
          padding: 10px 15px; 
          font-size: 14px; 
          cursor: pointer;
          border-radius: 5px;
        "
        onclick="window.downloadQuotationPDF()"
      >
        📥 Download PDF
      </button>
    </div>
    `;

        newTab.document.body.insertAdjacentHTML("beforeend", buttonHtml);

        // 📌 PDF İndirme Fonksiyonunu Tanımla
        newTab.window.downloadQuotationPDF = function () {
          if (typeof doc === "undefined") {
            console.error("❌ Hata: 'doc' değişkeni tanımlı değil!");
            return;
          }

          const fileName = `Quotation_${quotingToolData.quoteNo || "N-A"}_${safeText(
              quotingToolData.customer || "Customer"
          )}.pdf`;
          doc.save(fileName);
        };
      }
    }, 300);
  };

  const clickEditHandler = React.useCallback(
      (id) => {
        navigate(`/quoting-tool/create-quoting-tool/details?id=${id}`);
      },
      [navigate]
  );

  const clickDeleteHandler = React.useCallback(async (event, id) => {
    event?.stopPropagation?.();

    try {
      if (!await appConfirm("Are you sure you want to delete this quote? This action cannot be undone.")) {
        return;
      }

      await CrudService.deleteQuotingTool(id);
      setData((previousData) =>
          previousData.filter((item) => item.id !== id)
      );

      setNotification({
        value: true,
        text: "Quote tool deleted successfully.",
      });

      setTimeout(() => {
        setNotification({ value: false, text: "" });
      }, 5000);
    } catch (error) {
      console.error("Error deleting quotingTool:", error);

      setNotification({
        value: true,
        text: "Failed to delete quote tool. Please try again later.",
      });

      setTimeout(() => {
        setNotification({ value: false, text: "" });
      }, 5000);
    }
  }, []);

  // Liste görünümü için tarih, rota ve servis özetleri
  const buildListFields = (quote, detailedTransportType) => {
    const formatListDate = (value) => {
      if (!value) return "";
      const date = new Date(value);
      return Number.isNaN(date.getTime())
          ? ""
          : date.toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" });
    };
    const place = (...parts) =>
        parts.map((part) => String(part || "").trim()).filter(Boolean).join(", ");
    const firstCarrier = Array.isArray(quote.carriers) ? quote.carriers[0] || {} : {};
    const origin =
        place(firstCarrier.pol, firstCarrier.polCountry) ||
        place(quote.pol, quote.polCountry) ||
        place(quote.originAirport) ||
        place(quote.pickupLocation?.city, quote.pickupLocation?.country) ||
        place(quote.pickupCity);
    const destination =
        place(firstCarrier.pod, firstCarrier.podCountry) ||
        place(quote.pod, quote.podCountry) ||
        place(quote.destinationAirport) ||
        place(quote.deliveryLocation?.city, quote.deliveryLocation?.country) ||
        place(quote.deliveryCity);
    const validUntil = quote.estimatedCloseDate ? new Date(quote.estimatedCloseDate) : null;
    const openStatus = ["", "pending", "sent"].includes(String(quote.status || "").toLowerCase());
    const serviceSummary = [quote.quoteType, detailedTransportType, quote.service]
        .map((part) => String(part || "").trim())
        .filter((part) => part && part !== "-")
        .join(" · ");

    return {
      quoteDateText: formatListDate(quote.quoteDate || quote.createdAt || quote.created_at),
      validUntilText: formatListDate(quote.estimatedCloseDate),
      validUntilSort: validUntil && !Number.isNaN(validUntil.getTime()) ? validUntil.getTime() : 0,
      isExpired: Boolean(validUntil && validUntil.getTime() < Date.now() && openStatus),
      origin,
      destination,
      routeSearch: `${origin} ${destination}`,
      serviceSummary: serviceSummary || "—",
      serviceSearch: `${quote.selectedTransportType || ""} ${serviceSummary}`,
    };
  };

  const tableData = React.useMemo(
      () =>
          data.map((quotingToolRow) => {
            let salesRepDisplay = "-";

            if (Array.isArray(quotingToolRow.salesRep)) {
              const names = quotingToolRow.salesRep.map((salesRepItem) => {
                const salesRepId =
                    typeof salesRepItem === "object"
                        ? salesRepItem?.id || salesRepItem?._id
                        : salesRepItem;

                const match = salesRepOptions.find(
                    (representative) =>
                        String(representative.id) === String(salesRepId)
                );

                return (
                    match?.name ||
                    salesRepItem?.name ||
                    salesRepItem?.label ||
                    "Unknown"
                );
              });

              salesRepDisplay = names.join(", ");
            }

            const sellingOffers = buildSellingOffers(
                quotingToolRow,
                tableExchangeRates
            );

            const sellingRateSearch = sellingOffers
                .flatMap((offer) => [
                  offer.carrierName,
                  offer.amount === null
                      ? "Rate unavailable"
                      : formatAmountMaxTwo(offer.amount),
                  offer.currency,
                  ...(offer.breakdown || []).flatMap((item) => [
                    item.label,
                    formatAmountMaxTwo(item.originalAmount),
                    item.originalCurrency,
                  ]),
                ])
                .filter(Boolean)
                .join(" ");

            const detailedTransportType =
                quotingToolRow.selectedTransportType === "Ocean"
                    ? quotingToolRow.oceanTransportType
                    : quotingToolRow.selectedTransportType === "Air"
                        ? quotingToolRow.airTransportType
                        : quotingToolRow.selectedTransportType === "Road"
                            ? quotingToolRow.roadTransportType
                            : quotingToolRow.transportType || "-";

            return {
              id: quotingToolRow.id,
              quoteNo:
                  quotingToolRow.quoteNo ||
                  "No Quote Number",
              selectedTransportType:
                  quotingToolRow.selectedTransportType || "-",
              customer:
                  quotingToolRow.customer ||
                  "Unknown Customer",
              quoteType: quotingToolRow.quoteType || "-",
              transportType: detailedTransportType || "-",
              status: quotingToolRow.status,
              service: quotingToolRow.service || "-",
              sallingRate: quotingToolRow.sallingRate,
              sellingOffers,
              sellingRateSearch,
              salesRep: salesRepDisplay,
              ...buildListFields(quotingToolRow, detailedTransportType),
              actions: "",
              rawQuote: quotingToolRow,
            };
          }),
      [data, salesRepOptions, tableExchangeRates]
  );

  const MODE_LABELS = {
    Road: "Road",
    Ocean: "Ocean",
    Air: "Air",
    Multimodal: "Multimodal",
    ProjectCargo: "Project Cargo",
  };

  const [modeFilter, setModeFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");

  const modeOptions = React.useMemo(() => {
    const counts = {};
    tableData.forEach((row) => {
      counts[row.selectedTransportType] = (counts[row.selectedTransportType] || 0) + 1;
    });
    return [
      { value: "all", label: `All (${tableData.length})` },
      ...Object.keys(MODE_LABELS)
          .filter((mode) => counts[mode])
          .map((mode) => ({ value: mode, label: `${MODE_LABELS[mode]} (${counts[mode]})` })),
    ];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tableData]);

  const visibleRows = React.useMemo(
      () =>
          tableData.filter(
              (row) =>
                  (modeFilter === "all" || row.selectedTransportType === modeFilter) &&
                  (statusFilter === "all" || String(row.status || "Pending") === statusFilter)
          ),
      [tableData, modeFilter, statusFilter]
  );

  const subtleText = { fontSize: 12, color: CRM_COLORS.subtext, lineHeight: 1.4 };
  const strongText = { fontSize: 13, fontWeight: 600, color: CRM_COLORS.text, lineHeight: 1.4 };

  const headerLabel = (label) => (
      <span style={{ fontWeight: 700, fontSize: "12px", letterSpacing: "0.3px" }}>{label}</span>
  );

  const dataTableData = {
    columns: [
      {
        Header: () => headerLabel("QUOTE"),
        accessor: "quoteNo",
        Cell: ({ row }) => (
            <MDBox>
              <MDBox
                  component="button"
                  type="button"
                  onClick={() => clickEditHandler(row.original.id)}
                  sx={{
                    border: 0,
                    p: 0,
                    background: "none",
                    cursor: "pointer",
                    fontFamily: "inherit",
                    fontSize: 13,
                    fontWeight: 700,
                    color: CRM_COLORS.blue,
                    "&:hover": { textDecoration: "underline" },
                  }}
              >
                {row.original.quoteNo}
              </MDBox>
              <MDTypography sx={subtleText}>{row.original.quoteDateText}</MDTypography>
            </MDBox>
        ),
      },
      {
        Header: () => headerLabel("CUSTOMER"),
        accessor: "customer",
        Cell: ({ value }) => (
            <MDTypography sx={{ ...strongText, fontWeight: 500 }}>
              <TruncatedText text={value} max={34} />
            </MDTypography>
        ),
      },
      {
        Header: () => headerLabel("SERVICE"),
        accessor: "serviceSearch",
        Cell: ({ row }) => (
            <MDBox>
              <MDTypography sx={strongText}>
                {MODE_LABELS[row.original.selectedTransportType] || row.original.selectedTransportType}
              </MDTypography>
              <MDTypography sx={subtleText}>{row.original.serviceSummary}</MDTypography>
            </MDBox>
        ),
      },
      {
        Header: () => headerLabel("ROUTE"),
        accessor: "routeSearch",
        Cell: ({ row }) =>
            row.original.origin || row.original.destination ? (
                <MDBox>
                  <MDTypography sx={{ ...strongText, fontWeight: 500 }}>
                    <TruncatedText text={row.original.origin || "—"} max={30} />
                  </MDTypography>
                  <MDTypography sx={subtleText}>
                    → <TruncatedText text={row.original.destination || "—"} max={30} />
                  </MDTypography>
                </MDBox>
            ) : (
                <MDTypography sx={subtleText}>—</MDTypography>
            ),
      },
      {
        Header: () => headerLabel("SELLING OFFER"),
        accessor: "sellingRateSearch",
        disableSortBy: true,
        Cell: ({ row }) => {
          const offers = Array.isArray(row.original.sellingOffers)
              ? row.original.sellingOffers
              : [];

          if (!offers.length) {
            return <MDTypography sx={subtleText}>—</MDTypography>;
          }

          return (
              <MDBox display="flex" flexDirection="column" gap={0.5} sx={{ minWidth: 170 }}>
                {offers.map((offer) => {
                  const amountText =
                      offer.amount === null
                          ? tableRatesLoading
                              ? `Converting to ${offer.currency}...`
                              : `Rate unavailable (${offer.currency})`
                          : `${formatAmountMaxTwo(offer.amount)} ${offer.currency}`;

                  const breakdown = offer.breakdown || [];

                  const tooltipContent = (
                      <div style={{ minWidth: 240, padding: "4px 2px" }}>
                        {offer.carrierName && (
                            <div style={{ fontWeight: 700, marginBottom: 6 }}>{offer.carrierName}</div>
                        )}
                        {breakdown.map((item, itemIndex) => (
                            <div
                                key={item.key || `${offer.key}-${itemIndex}`}
                                style={{ display: "flex", justifyContent: "space-between", gap: 16, marginBottom: 3 }}
                            >
                              <span>{item.label}</span>
                              <strong style={{ whiteSpace: "nowrap" }}>
                                {formatAmountMaxTwo(item.originalAmount)} {item.originalCurrency}
                              </strong>
                            </div>
                        ))}
                        {breakdown.length > 0 && (
                            <div
                                style={{
                                  borderTop: "1px solid rgba(255,255,255,0.35)",
                                  marginTop: 6,
                                  paddingTop: 6,
                                  display: "flex",
                                  justifyContent: "space-between",
                                  gap: 16,
                                  fontWeight: 700,
                                }}
                            >
                              <span>Offer</span>
                              <span style={{ whiteSpace: "nowrap" }}>{amountText}</span>
                            </div>
                        )}
                      </div>
                  );

                  return (
                      <Tooltip
                          key={offer.key}
                          title={breakdown.length > 0 ? tooltipContent : amountText}
                          arrow
                          placement="top"
                      >
                        <MDBox display="flex" alignItems="baseline" gap={1} sx={{ cursor: "help" }}>
                          {offers.length > 1 && offer.carrierName && (
                              <MDTypography sx={{ ...subtleText, minWidth: 70 }}>
                                <TruncatedText text={offer.carrierName} max={14} />
                              </MDTypography>
                          )}
                          <MDTypography
                              sx={{
                                ...strongText,
                                whiteSpace: "nowrap",
                                color: offer.amount === null ? CRM_COLORS.amber : CRM_COLORS.text,
                              }}
                          >
                            {amountText}
                          </MDTypography>
                        </MDBox>
                      </Tooltip>
                  );
                })}
              </MDBox>
          );
        },
      },
      {
        Header: () => headerLabel("VALID UNTIL"),
        accessor: "validUntilSort",
        Cell: ({ row }) => (
            <MDBox>
              <MDTypography sx={{ ...strongText, fontWeight: 500 }}>
                {row.original.validUntilText || "—"}
              </MDTypography>
              {row.original.isExpired && (
                  <MDTypography sx={{ fontSize: 11.5, fontWeight: 600, color: CRM_COLORS.red }}>
                    Expired
                  </MDTypography>
              )}
            </MDBox>
        ),
      },
      {
        Header: () => headerLabel("STATUS"),
        accessor: "status",
        Cell: ({ row }) => (
            <StatusCell
                row={row.original}
                onLocalUpdate={handleLocalStatusUpdate}
            />
        ),
      },
      {
        Header: () => headerLabel("ACTIONS"),
        accessor: "actions",
        disableSortBy: true,
        Cell: ({ row }) => (
            <CrmRowActions
                onEdit={() => clickEditHandler(row.original.id)}
                onDelete={(event) => clickDeleteHandler(event, row.original.id)}
                editTitle="Edit quotation"
                deleteTitle="Delete quotation"
            >
              <Tooltip title="Download PDF">
                <IconButton
                    onClick={() => generatePDF(row.original.rawQuote)}
                    sx={{
                      width: 32,
                      height: 32,
                      borderRadius: "8px",
                      color: CRM_COLORS.text,
                      border: `1px solid ${CRM_COLORS.cardBorder}`,
                      background: CRM_COLORS.white,
                      "&:hover": { background: CRM_COLORS.muted, borderColor: CRM_COLORS.navy },
                      "& svg": { fontSize: "18px !important" },
                    }}
                >
                  <PictureAsPdfOutlined />
                </IconButton>
              </Tooltip>
            </CrmRowActions>
        ),
      },
    ],
    rows: visibleRows,
  };

  const clickAddHandler = () => {
    navigate("/quoting-tool/create-quoting-tool/details"); // Yeni shipment ekleme sayfasına yönlendiriyoruz
  };

  const hasFilters = modeFilter !== "all" || statusFilter !== "all";

  return (
      <DashboardLayout>
        <DashboardNavbar/>
        <CrmPage>
          <CrmPageHeader
              title="Quotations"
              subtitle="Prepare, send and track freight quotations. Open a quote to edit it or download the customer PDF."
              actions={
                <CrmActionButton icon={<Add />} onClick={clickAddHandler}>
                  New Quotation
                </CrmActionButton>
              }
          />

          {notification.value && <CrmNotice tone="red">{notification.text}</CrmNotice>}

          <CrmFilterBar>
            <MDBox display="flex" flexDirection="column" gap={0.75}>
              <MDTypography sx={{ fontSize: 11.5, fontWeight: 700, color: CRM_COLORS.subtext, letterSpacing: "0.3px" }}>
                TRANSPORT MODE
              </MDTypography>
              <SegmentedTabs options={modeOptions} value={modeFilter} onChange={setModeFilter} />
            </MDBox>

            <MDBox display="flex" flexDirection="column" gap={0.75} ml={{ md: "auto" }}>
              <MDTypography sx={{ fontSize: 11.5, fontWeight: 700, color: CRM_COLORS.subtext, letterSpacing: "0.3px" }}>
                STATUS
              </MDTypography>
              <SegmentedTabs
                  options={[{ value: "all", label: "All" }, ...statusOptions.map((o) => ({ value: o.value, label: o.label }))]}
                  value={statusFilter}
                  onChange={setStatusFilter}
              />
            </MDBox>
          </CrmFilterBar>

          <TableCard
              title="Quotation list"
              summary={
                hasFilters
                    ? `${visibleRows.length} of ${tableData.length} quotations match the selected filters`
                    : `${tableData.length} quotations`
              }
          >
            <DataTable table={dataTableData} canSearch/>
          </TableCard>
        </CrmPage>
        <Footer/>
      </DashboardLayout>
  );
}
export default QuotingToolTable;
